/**
 * Every user-facing string on the gate, the shell and the working screens, in one module.
 *
 * WHY THIS EXISTS AND WHAT IT IS NOT
 *   `CODING_STANDARDS.md` section 3.3 requires that no user-facing string literal sits in
 *   a component, because full Arabic with right-to-left layout is a committed requirement
 *   and it is only achievable if the rule holds from the first screen. The translation
 *   catalogue and the lint rule that enforces it are NOT in this repository yet, and the
 *   components that existed before this change carried their strings inline.
 *
 *   This module is the honest halfway step: one place that holds the English, ready to
 *   become the `en` catalogue when the i18n work order lands, and reachable by a test.
 *   It is not a translation layer and it is not pretending to be one. Reported as a gap
 *   rather than filled, per CODING_STANDARDS section 6 item 7.
 *
 * THE TERMINOLOGY SEAM (M3)
 *   The configuration milestone makes role labels and rendered terms tenant-editable at
 *   runtime: the serializer layer reads `terminology_override` rows and the vocabulary
 *   enum tokens stay canonical (Ruling 16 #4). This module is where those overrides
 *   arrive: when the configuration API lands, a screen that needs a rendered term asks
 *   here, and here can hand back either the tenant's override or this default. Until
 *   then every default lives here and nowhere else, which is the seam the backend
 *   overrides plug into.
 */

/**
 * The standards alignment line, approved by Mahmoud on 2026-09-20.
 *
 * 🛑 This is a statement of ALIGNMENT and it is load bearing legally, not stylistically.
 * It says "built on the discipline of". It does NOT say compliant, certified, accredited
 * or conforming, because nothing here is certified. It is rendered as text with no logo,
 * no badge and no certification mark of any kind: the ISO mark may not be used as a logo
 * even by organisations that hold the certificate.
 *
 * `strings.test.ts` asserts this exact string, character for character. If a future change
 * needs different wording, that is Mahmoud's decision and the test is the record of it.
 */
export const STANDARDS_LINE =
  "Built on the discipline of ISO 9001:2015 quality management, " +
  "ISO 10005 quality planning and ISO 19011 audit practice.";

export const strings = {
  /** The product name. Rendered in capitals by CSS so it is still read as a word. */
  productName: "Qualify",

  gate: {
    descriptor: "Enterprise Quality Management System",
    tagline: "Where the evidence collects.",
    signIn: "Sign in",
    signInFailed: "The sign-in did not complete. Please try again.",
    standards: STANDARDS_LINE,
    colophon: "An Evoke Solutions product",
    markAlt: "",
  },

  completing: {
    message: "Completing sign-in...",
  },

  status: {
    checking: "Checking the service...",
    failed: "The service did not answer.",
  },

  shell: {
    signOut: "Sign out",
    loading: "Loading your account...",
    failed: "Your account could not be loaded.",
    greeting: (name: string): string => `Welcome, ${name}`,
    identityLabel: "Your account",
    signedInAs: "Signed in as",
    email: "Email",
    subject: "User identifier",
    emailAbsent: "Not provided",
    skipToContent: "Skip to content",
  },

  /* ------------------------------------------------------------------ */
  /* Navigation. Rendered from the answers the API gives about records,  */
  /* never from anything the client decides about its own permissions.   */
  /* ------------------------------------------------------------------ */
  nav: {
    primary: "Primary",
    /* The persona landing (M6 Station 2b): pinned at the top of every sidebar. */
    home: "Home",
    dashboard: "Dashboard",
    itps: "Inspection and test plans",
    schedules: "Schedules",
    inspections: "Inspections",
    findings: "Findings",
    actions: "Actions",
    configuration: "Configuration",
    guide: "Guide",
    /* The sidebar (M4 Phase 2). One tree for every persona; scope-neutral labels. */
    calendar: "Calendar",
    inspectionsRfis: "Inspections & RFIs",
    findingsPunch: "Findings & Punch Lists",
    nonConformities: "Non-Conformities & Actions",
    projectsContracts: "Projects & Contracts",
    partiesContractors: "Parties & Contractors",
    lessonsLearned: "Quality Lessons Learned",
    myEvaluations: "My Evaluations",
    myWork: "My Work",
    programEngineering: "Engineering Surveillance Program",
    programProcurement: "Procurement Surveillance Program",
    programConstruction: "Construction Surveillance Program",
    programExternalAudits: "External Audits Program",
    sidebarToggle: "Show or hide the navigation",
    railExpand: "Expand the navigation",
    railCollapse: "Collapse the navigation to icons",
    /* The phone frame (W-F shell wave): the side menu is a drawer behind the top bar. */
    drawerOpen: "Open the navigation",
    drawerClose: "Close the navigation",
    groups: {
      overview: "Overview",
      myWork: "My work",
      queues: "My queues",
      planning: "Planning",
      programs: "Programs",
      registers: "Registers",
      portfolio: "Portfolio",
      intelligence: "Intelligence",
      workspace: "Workspace",
    },
    /* The per-persona shapes (workflow contract 3.5). The same routes, shaped per
       principal class; nav is a workflow aid, never a permission change. */
    myDashboard: "My Dashboard",
    myCalendar: "My Calendar",
    myItps: "My ITPs",
    inspectionsOwn: "Inspections (own)",
    findingsToAnswer: "Findings to answer",
    schedulesOwn: "Schedules (own)",
    myEvaluation: "My Evaluation",
    myInspections: "My Inspections",
    myFindings: "My Findings",
    programsRead: "Programs (read)",
    planningCandidates: "Planning candidates",
    /* Wave W1, menus from the flows: every queue entry opens its register PRE-FILTERED
       to exactly what is waiting on the person (R13), so the labels say "to do". */
    tasks: "Tasks and inbox",
    tasksOpen: (count: number): string => `, ${String(count)} open`,
    queueItpReview: "Plans to review",
    queueScheduleSign: "Schedules to sign",
    queueResultApproval: "Results to approve",
    queueRectifications: "Rectifications to verify",
    queueResponses: "Responses to decide",
    queueMyInspections: "My open inspections",
    witnessDocket: "Witness docket",
    findingsRaised: "Findings raised",
    openFindings: "Open findings",
    yourNonConformities: "Your non-conformities",
    /* Wave W2: the RFI register (R10). */
    rfis: "RFIs",
    myRfis: "My RFIs",
    /* Wave b12 frontend item 3: the account and its preferences. */
    account: "Your account",
    outbox: "Outbox",
    standards: "Standards library",
    credentials: "Credentials",
    audit: "Audit register",
  },

  /* ------------------------------------------------------------------ */
  /* The shell's working surface (M5 P1b): theme toggle, notification    */
  /* bell placeholder, account menu.                                     */
  /* ------------------------------------------------------------------ */
  shellUi: {
    themeLabel: "Theme",
    lightTheme: "Light theme",
    darkTheme: "Dark theme",
    notifications: "Notifications",
    notificationsPlaceholder: "Notifications arrive in the next station",
    notificationsEmpty: "Nothing waiting for you.",
    notificationsUnread: "unread",
    notificationsMarkAll: "Mark all read",
    notificationsFailure: "The notification center could not be read. It retries in a minute.",
    /* Wave W2 (Mahmoud's UAT 2026-10-10): the bell follows the tasks. */
    notificationsWaiting: "Waiting on you",
    notificationsAttended: (count: number): string => `Done (${String(count)})`,
    notificationsAttendedLede:
      "Handoffs whose task has been completed. They no longer count as unread.",
    notificationsDoneOn: (when: string): string => (when === "" ? "Done" : `Done ${when}`),
    notificationsViewAll: "View all tasks and inbox",
    accountMenu: "Account",
  },

  /* ------------------------------------------------------------------ */
  /* Quick actions menu (Wave H batch 2): top bar "+" button.           */
  /* ------------------------------------------------------------------ */
  quickActions: {
    buttonLabel: "Quick actions",
    menuLabel: "Quick actions menu",
    raiseRfi: "Raise an RFI",
    proposeDate: "Propose a date",
    attachDocument: "Attach a document",
    requestInspection: "Request an inspection",
    planActivity: "Plan an activity",
    recordObservation: "Record an observation",
    registerProject: "Register a project",
    conductInspection: "Conduct my next agreed inspection",
    myTasks: "My tasks",
  },

  /* ------------------------------------------------------------------ */
  /* The breadcrumb trail (wave W2): one trail under the shell bar on     */
  /* every screen, Home / register / record, the register kept as you     */
  /* left it.                                                             */
  /* ------------------------------------------------------------------ */
  crumbs: {
    label: "Where you are",
    record: "Record",
    project: "Project",
    contract: "Contract",
    plan: "Plan",
    schedule: "Schedule",
    activity: "Activity",
    rfi: "RFI",
    inspection: "Inspection",
    finding: "Finding",
    action: "Action",
    notFound: "Not found",
  },

  /* ------------------------------------------------------------------ */
  /* A record's own open tasks (wave W2: "View open tasks" means THIS     */
  /* record's tasks).                                                     */
  /* ------------------------------------------------------------------ */
  /* Wave Experience A, item 3: the rows a queue holds that the account cannot act on. */
  fyi: {
    summary: (count: number): string => `For your information (${String(count)})`,
    lede: "Nothing here waits on you. Mark a note done once you have read it.",
  },

  recordTasks: {
    heading: "Your open tasks here",
    lede: "Tasks of yours about this record and the records under it.",
    loading: "Reading your tasks on this record...",
    failed: "Your tasks on this record could not be read. The full list is in Tasks and inbox.",
    empty: (kind: string): string => `You have no open task on this ${kind}.`,
    more: (count: number): string =>
      count === 1 ? "See it in Tasks and inbox" : `See all ${String(count)} in Tasks and inbox`,
    railNone: (kind: string): string => `No open task of yours on this ${kind}.`,
    project: "project",
    contract: "contract",
    /* Wave Experience A, item 2: the bible leads with the account's pending actions. */
    pendingHeading: "Your pending actions on this project",
    pendingLede:
      "Your open tasks on this project and the records under it, newest first. Each opens where it is done.",
  },

  /* ------------------------------------------------------------------ */
  /* The portfolio registers (M4): projects, contracts, parties.          */
  /* ------------------------------------------------------------------ */
  portfolio: {
    projectsHeading: "Projects & Contracts",
    projectsLede:
      "The projects you may see, and the main contract that carries the work on each. One project, one main contract, one main contractor.",
    projectsEmptyTitle: "No projects yet",
    projectsEmptyBody: "A project appears here once it is opened and you are within its view.",
    projectsRegisterTitle: "Projects",
    contractsHeading: "Main contracts",
    contractsEmptyTitle: "No contracts yet",
    contractsEmptyBody: "Contracts appear here once they are registered against your projects.",
    partiesHeading: "Parties & Contractors",
    partiesLede:
      "The main contractors of your projects, with their computed quality numbers. Sub-contractors and vendors are listed for information on their main contract's page. Percentages only.",
    partiesEmptyTitle: "No evaluable parties yet",
    partiesEmptyBody: "A counterparty with inspectable work appears here once its records exist.",
    columnProject: "Project",
    columnContract: "Contract",
    columnParty: "Party",
    columnCategory: "Category",
    columnLocation: "Location",
    columnLead: "Lead QA/QC",
    columnDates: "Dates",
    columnContractor: "Contractor",
    columnStatus: "Status",
    columnInspections: "Inspections conducted",
    columnPassRate: "First-time pass rate",
    columnOverdue: "Overdue actions",
    columnDossier: "Dossier",
    subContract: "Sub-contract",
    openDossier: "Open dossier",
    dossierNote:
      "The per-contractor dossier lives on the evaluations screen; a dedicated dossier page per party is future work.",
    /* The front door (M5 P4). */
    createProject: "Create project",
    createProjectHeading: "Create project",
    createNameLabel: "Name",
    createNumberLabel: "Project number",
    createParentLabel: "Parent project (optional)",
    createDisciplinesLabel: "Disciplines (the project's quality context)",
    createStagesLabel: "Stages",
    createStartLabel: "Start date",
    createEndLabel: "End date",
    createContractScopeLabel: "Discipline scope (a subset of the project's)",
    createSubmit: "Register",
    createDone: "Registered.",
    qualityContextHeading: "Quality context (disciplines)",
    calendarPeriodsNote:
      "Calendar periods stay configuration-side; open the calendar to read the periods this project's programme is evaluated against.",
    breadcrumbRoot: "Projects & Contracts",
    /* Wave W0: one main contract per project; sub-contracts are an FYI. */
    mainContractHeading: "Main contract",
    mainContractNone: "No main contract is recorded for this project.",
    /* The staffing truth (Mahmoud's rulings 2026-10-11), stated where it matters. */
    staffingRequiredHeading: "No QA/QC engineer on this project",
    staffingRequiredBody:
      "Nobody in the QA/QC engineer role covers this project or its main contract. Inspection requests on it reach no engineer, and inspection results skip the engineer's step of the approval chain until one is assigned.",
    staffingRequiredAct:
      "Assign a QA/QC engineer here. The staffing task closes by itself when you do.",
    staffingRequiredNoRight:
      "Your role does not assign people here. The QC lead has a task to staff this project.",
    descriptionLabel: "Description",
    subContractsHeading: "Sub-contracts and vendors (for information)",
    subContractsLede:
      "The main contractor is the operator's only contracting focal point. These are the packages under the main contract, listed so the operator knows them; they are not separate contractors here.",
    subContractsEmpty: "No sub-contract is registered under this main contract.",
    subContractUnder: (parent: string): string => `Under ${parent}`,
    subContractNote: (main: string): string =>
      `This is a sub-contract, shown for information. The main contractor answers for it under ${main}.`,
    /* Wave W2 (Director's finding 2026-10-10): a contractor with nobody to sign in. */
    noPortalUsers:
      "This contractor has no portal users yet. Their tasks wait until their people are registered.",
    noPortalUsersNamed: (party: string): string =>
      `${party} has no portal users yet. Their tasks wait until their people are registered.`,
    columnPortalUsers: "Portal users",
    portalUsersNone: "None yet",
    portalUsersNoneNote: "Its tasks wait until its people are registered.",
    contractsAndPartiesHeading: "Contracts and parties",
    qualitySummaryHeading: "Quality summary",
    scheduleAndCalendarHeading: "Schedule and calendar",
    registersHeading: "Registers",
    tpiContract: "TPI contract",
    openCalendarForProject: "Open calendar for this project",
    itpsRegisterHeading: "ITPs",
    verificationsRegisterHeading: "Verifications",
    findingsRegisterHeading: "Findings",
    punchesRegisterHeading: "Punch items",
    historyHeading: "History",
    findingsBySeverity: "Findings by severity",
    verificationsByState: "Verifications by state",
    punchItemsSummary: "Punch items",
    rfisByState: "Open RFIs by state",
    openLabel: "Open",
    closedLabel: "Closed",
    verificationStatePlanned: "Planned",
    verificationStateAgreed: "Agreed",
    verificationStateConducted: "Conducted",
    verificationStateAwaitingApproval: "Awaiting approval",
    verificationStateAwaitingClose: "Awaiting close",
    verificationStateClosed: "Closed",
    findingsEmpty: "No findings recorded yet",
    verificationsEmpty: "No verifications recorded yet",
    punchesEmpty: "No punch items recorded yet",
    rfisEmpty: "No open RFIs",
  },

  /* ------------------------------------------------------------------ */
  /* The front door as amended (M6 Station 2b): register a project with   */
  /* its awarded contractor, approve it, staff it. Plain language only.   */
  /* ------------------------------------------------------------------ */
  portalAccounts: {
    createButton: "Create portal account",
    createHeading: (party: string): string => `Create portal account for ${party}`,
    fullNameLabel: "Full name",
    emailLabel: "Email",
    usernameLabel: "Username",
    submit: "Create account",
    submitBusy: "Creating...",
    cancel: "Cancel",
    fieldLabels: { full_name: "Full name", email: "Email", username: "Username" },
    absent: "This contractor could not be found, or you may not create accounts for it.",
    doneHeading: "Account created",
    doneLine: (name: string, username: string): string =>
      `${name} can sign in as ${username} with the temporary password below.`,
    doneLineEmailed: (name: string, username: string, email: string): string =>
      `${name} can sign in as ${username}. The temporary password was emailed to ${email}; it is not shown here.`,
    passwordLabel: "Temporary password",
    copy: "Copy password",
    copied: "Copied to your clipboard.",
    copyFailed: "Your browser would not copy it. Select the password and copy it by hand.",
    changeNote: "This password must be changed at first sign-in.",
    onceNote:
      "It is shown only here and only now. Closing this window discards it permanently, and it cannot be shown again.",
    deliveryNote: "In a coming wave, email delivery will replace this handover.",
    done: "Close and discard the password",
    doneEmailed: "Close",
    pickLegend: "Contractor accounts",
    pickIntro:
      "These accounts can sign in for this contractor. Choose the one to give sight of this project's contract, or create a new account now.",
    blockHeading: "Connected accounts",
    blockLede:
      "The people who can sign in for this contractor, and the contracts each one may see. An account sees nothing until a contract is assigned to it.",
    blockEmpty: "This contractor has no connected accounts yet.",
    blockEmptyOnProject:
      "No account of this contractor is assigned to a contract of this project yet.",
    othersHeading: "Other accounts of this contractor",
    othersLede:
      "These accounts can sign in for this contractor but see nothing of this project. Assign one of the project's contracts to give an account sight of it.",
    noContracts: "No contract assigned: this account sees nothing yet.",
    assignedLabel: "Assigned contracts",
    createdLine: (by: string | null, on: string): string =>
      by === null ? `Created on ${on}.` : `Created by ${by} on ${on}.`,
    historyNote:
      "Who assigned which contract, and when, is not yet recorded for display. Only who created the account is shown.",
    contractLine: (humanId: string, title: string | null): string =>
      title === null ? humanId : `${humanId}, ${title}`,
    assignLabel: "Contract to assign",
    assignChoose: "Choose a contract",
    assignButton: "Assign contract",
    assignBusy: "Assigning...",
    assignNone: "Every contract of this contractor is already assigned to this account.",
    assignDone: (name: string, contract: string): string => `${name} can now see ${contract}.`,
    revokeButton: (contract: string): string => `Revoke ${contract}`,
    revokeHeading: "Revoke contract access",
    revokeConfirm: (name: string, contract: string): string =>
      `${name} will stop seeing ${contract} and everything recorded on it. Nothing is deleted, and you can assign the contract again later.`,
    revokeSubmit: "Revoke access",
    revokeBusy: "Revoking...",
    revokeDone: (name: string, contract: string): string => `${name} no longer sees ${contract}.`,
    revokeAbsent:
      "This access could not be removed. Only a holder of the portal account permission, or the approver of the project the contract is registered on, can remove it, and it may already have been removed. Close this window and reload the page to see the current list.",
    pickFallbackName: "the chosen account",
    pickNone: "Assign no account for now",
    pickLoading: "Loading the contractor's accounts...",
    pickFailed: "The contractor's accounts could not be listed, so none can be assigned here.",
    pickEmpty: "This contractor has no accounts yet. Create one to assign it to this contract.",
    pickCreatedLine: (name: string): string => `${name} was created and will be assigned.`,
    accountLine: (name: string, username: string): string => `${name} (${username})`,
    assignFailed: (name: string): string =>
      `The project was registered, but ${name} could not be assigned to its contract. Assign the account from the contractor's page.`,
    newContractorNote:
      "Accounts are created after the contractor is approved. Register the project now; once the contractor is approved, create its accounts from the contractor's page or the project page.",
  },
  frontDoor: {
    projectSection: "The project",
    contractSection: "The main contract",
    contractorSection: "The awarded contractor",
    documentsSection: "Documents",
    costCentreLabel: "Cost centre (optional)",
    descriptionLabel: "Description (optional)",
    contractTitleLabel: "Contract title",
    contractReferenceNote:
      "Qualify assigns the contract its reference number automatically when the project is registered.",
    contractScopeLabel: "Scope description (optional)",
    contractDisciplinesLabel: "Contract disciplines (from the project's disciplines)",
    contractDisciplinesEmpty:
      "Pick the project's disciplines first; the contract scope is chosen from them.",
    contractStartLabel: "Contract start date (optional)",
    contractEndLabel: "Contract end date (optional)",
    contractValueLabel: "Contract value (optional)",
    contractCurrencyLabel: "Currency (optional)",
    contractorModeQuestion: "Is the contractor already registered in Qualify?",
    contractorModeExisting: "Choose an existing contractor",
    contractorModeNew: "Register a new contractor",
    contractorModeNewNotYours:
      "Your account cannot register a new contractor: by default the Power user role holds that, or an account given it as a grant. Choose an existing contractor, or ask a Power user to register it first.",
    contractorPickLabel: "Contractor",
    contractorPickEmpty:
      "No contractors are in the register yet. Register a new one with the option above.",
    newContractorName: "Contractor name",
    newContractorType: "Contractor type",
    newContractorRegistration: "Commercial registration number (optional)",
    newContractorCountry: "Country (optional)",
    newContractorContact: "Contact note (optional)",
    newContractorNoType:
      "A new contractor cannot be registered from this screen yet: the server does not send the contractor type reference this form needs. Choose an existing contractor instead, or ask your administrator.",
    attachLabel: "Add documents",
    attachHelper:
      "Optional. The files are attached to the project once it is registered. Files are not kept in the saved draft.",
    attachNone: "No documents chosen.",
    draftRestored: "Your unfinished form was restored.",
    clearDraft: "Clear the form",
    submit: "Register project",
    submitBusy: "Registering...",
    errorName: "Enter the project name.",
    errorNumber: "Enter the project number.",
    errorDates:
      "The end date is before the start date. Pick an end date on or after the start date.",
    errorContractDates:
      "The contract end date is before its start date. Pick an end date on or after the start date.",
    errorContractTitle: "Enter the contract title.",
    errorContractor: "Choose the awarded contractor, or register a new one.",
    errorNewContractorName: "Enter the new contractor's name.",
    errorNewContractorType: "Choose the contractor type.",
    errorValue:
      "Enter the contract value as a number with at most two decimals, for example 125000 or 125000.50.",
    countryNone: "Not set",
    currencyNone: "Not set",
    countryHelper: "Choose from the list; Qualify stores the country's standard two-letter code.",
    // The names a refused field is called by in the summary above the buttons.
    fieldNames: {
      name: "Project name",
      project_number: "Project number",
      description: "Description",
      category: "Category",
      location: "Location",
      cost_centre: "Cost centre",
      parent_project_id: "Parent project",
      start_date: "Start date",
      end_date: "End date",
      discipline_ids: "Disciplines",
      stage_ids: "Stages",
      "contract.title": "Contract title",
      "contract.scope_description": "Scope description",
      "contract.discipline_ids": "Contract disciplines",
      "contract.start_date": "Contract start date",
      "contract.end_date": "Contract end date",
      "contract.value_amount": "Contract value",
      "contract.value_currency": "Currency",
      "contract.contractor_party_id": "Contractor",
      "contract.new_contractor.name": "Contractor name",
      "contract.new_contractor.party_type_id": "Contractor type",
      "contract.new_contractor.registration_no": "Commercial registration number",
      "contract.new_contractor.country": "Country",
      "contract.new_contractor.contact_note": "Contact note",
    } as Record<string, string>,
    refusedNoPermission:
      "Your account does not hold the permission to register projects. Ask your administrator for the project registration permission.",
    registered: (projectId: string, contractor: string): string =>
      `${projectId} is registered with ${contractor} and is waiting for approval. Another approver has been asked to review it.`,
    attachFailed: (count: number): string =>
      `${String(count)} document(s) did not attach. Open the project and add them again.`,
    status: {
      PENDING_APPROVAL: "Waiting for approval",
      PENDING_ASSIGNMENT: "Approved, waiting for personnel",
      REGISTERED: "Registered",
    } as Record<string, string>,
    statusColumn: "Status",
    provenanceHeading: "Who did what",
    registeredBy: "Registered by",
    approvedBy: "Approved by",
    updatedBy: "Last changed by",
    notYet: "Not yet",
    /* The approval overlay. */
    approveHeading: "Approve project",
    approveIntro:
      "You are approving this project and its awarded contractor. Check the summary, then approve.",
    approveContractorLabel: "Awarded contractor",
    approveVendorPending:
      "The approved vendor list is checked when you approve. If the contractor is not yet approved for this scope, this screen asks you to approve it as a vendor in the same sitting.",
    vendorStepHeading: "Vendor approval needed",
    vendorStepIntro: "The contractor is not on the approved vendor list for these disciplines:",
    vendorStepConfirm: "Also approve the contractor as a vendor for these disciplines",
    vendorStepRequired:
      "Tick the vendor approval above to approve both, or cancel and hand the approval to a colleague.",
    assignStepRequired: "Pick a person and a role, or untick Assign personnel now.",
    engineerRequiredHeading: "A QA/QC engineer is required",
    engineerRequiredBody: (contract: string | null): string =>
      `Nobody on the team covers ${contract === null ? "the main contract" : `main contract ${contract}`} yet. Name at least one QA/QC engineer here: the approval cannot complete without one, because the contract's inspection requests would reach nobody.`,
    engineerRequiredReason: (role: string | null): string =>
      `Add a person in the ${role ?? "QA/QC engineer"} role to approve.`,
    assignRequired: "Pick a person and a role to assign.",
    vendorStepHelper:
      "Two recorded actions in one sitting: the vendor approval and the project approval.",
    /* The approval's outlook, read BEFORE the attempt (wave W1.5, finding 4). */
    outlookLoading: "Checking what this approval involves...",
    outlookFailed:
      "What this approval involves could not be read, so it cannot be sent yet. Close the dialog and open it again.",
    vendorClear: "The contractor is on the approved vendor list for this project's scope.",
    vendorStepIntroFor: (party: string): string =>
      `${party} is not on the approved vendor list for these disciplines:`,
    vendorBlockedHeading: "Vendor approval needed first",
    vendorBlockedNoVerb: "Your account does not hold vendor approval.",
    vendorBlockedSelf: "You registered this contractor, so you cannot also approve it as a vendor.",
    vendorBlockedWaits:
      "Approving this project also approves its contractor as a vendor for the disciplines above, so the project approval waits on the vendor approval.",
    vendorHolders: (holders: readonly string[]): string =>
      holders.length === 0
        ? "No role holds vendor approval yet."
        : `Vendor approval is held by: ${holders.join("; ")}.`,
    vendorHolderByGrant: (role: string): string => `${role} (given it as a personal grant)`,
    vendorHolderEveryone: "every account of your organisation",
    ableToComplete: (roles: readonly string[]): string =>
      `Who can take both steps in one sitting: ${roles.join("; ")}. Hand this approval to one of them.`,
    ableToCompleteNone:
      "Nobody who may approve this project also holds vendor approval, so it cannot be approved yet. Ask whoever administers permissions in your organisation to give vendor approval to one of this project's approvers.",
    approveBlockedCapability:
      "Your account cannot complete this approval: see Vendor approval needed first, above.",
    /* The team in the approval and in the assignment (wave W1.5, finding 5). */
    teamCurrentHeading: "Current team",
    teamNone: "Nobody is on this project's team yet.",
    teamAddHeading: "Add people",
    teamAddButton: "Add to the list",
    teamAddIncomplete: "Pick a person and a role, then add them to the list.",
    teamAlreadyOn: (who: string, role: string): string =>
      `${who} is already on the team as ${role}.`,
    teamAlreadyListed: (who: string, role: string): string =>
      `${who} is already on the list as ${role}.`,
    teamPendingHeading: "To be added",
    teamPendingRemove: (who: string): string => `Take ${who} off the list`,
    teamAddsNote:
      "Everyone listed here is ADDED to the team. Nobody already on the team is replaced; removing someone is its own step.",
    teamRemove: (who: string): string => `Remove ${who} from the team`,
    teamRemoveConfirm: (who: string, role: string): string =>
      `Remove ${who} (${role}) from this team? They lose this project's records that the role gave them. This is recorded in the project history.`,
    teamRemoveYes: "Yes, remove",
    teamRemoveNo: "Keep",
    teamRemoved: (who: string): string => `${who} was removed from the team.`,
    assignSubmitMany: (count: number): string =>
      count <= 1 ? "Assign" : `Assign ${String(count)} people`,
    assignedManyToast: (who: readonly string[]): string =>
      `${who.join(", ")} ${who.length === 1 ? "is" : "are"} added to the team.`,
    assignPartlyFailed: (names: readonly string[]): string =>
      `These were not added: ${names.join(", ")}. They are still listed.`,
    approveSubmit: "Approve project",
    approveWithVendorSubmit: "Approve project and vendor",
    approveBusy: "Approving...",
    selfApproval:
      "You registered this project, so another approver must act. The approval request is with the QC lead, or higher when the lead registered it.",
    vendorNotPermitted:
      "Your account cannot approve vendors. Ask for the vendor approval permission, or hand this approval to a colleague who holds it.",
    vendorSelfApproval:
      "You registered this contractor, so another approver must approve it as a vendor.",
    notPending: "This project is no longer waiting for approval.",
    approveAbsent: "This project is not one your account can approve.",
    approvedToast:
      "Project approved. The person who registered it has been asked to assign its personnel.",
    approvedRegisteredToast:
      "Project approved and registered. The contractor has been notified to author inspection and test plans.",
    assignNowLabel: "Assign personnel now (optional)",
    assignNowHelper:
      "Assigning now registers the project straight away; otherwise the person who registered it is asked to assign.",
    /* The assignment overlay. */
    assignHeading: "Assign personnel",
    assignIntro:
      "The team below is who is assigned now. Add one or more people of your organisation, each in a role; nobody already on the team is replaced.",
    assignPersonLabel: "Person",
    assignRoleLabel: "Role",
    // Wave INST ruling 5: an assignment for one discipline, or for every discipline.
    assignDisciplineLabel: "Discipline (optional)",
    assignDisciplineEvery: "Every discipline",
    assignDisciplineHelper:
      "Left on every discipline, the role covers every record here. Narrowed, it covers only records of that discipline: an inspector of another discipline is not offered them.",
    assignDisciplineOnly: (discipline: string): string => `${discipline} only`,
    assignDisciplineUnnamed: "one discipline",
    assignNoPeople:
      "No colleagues are visible to pick from yet. People appear here once they hold a role on a project you can see.",
    assignNoRoles:
      "No roles are visible to pick from yet. Roles appear here once someone holds one on a project you can see.",
    assignCandidatesNote:
      "The list shows the people and roles already working on the projects you can see.",
    assignSubmit: "Assign",
    assignBusy: "Assigning...",
    assignedToast: (person: string): string => `${person} is assigned.`,
    assignedRegisteredToast:
      "Assigned. The project is registered and the contractor has been notified to author inspection and test plans.",
    assignAbsent: "This assignment is not one your account can make.",
    actGone:
      "That task is already done, or it is no longer yours to act on. The actions you can take now are listed below.",
    /* The provenance block. */
    /* Documents on a project or contract (wave W1.5, finding 3b). */
    attachDialogHeading: "Attach documents",
    attachDialogIntro: (target: string): string =>
      `The documents are added to ${target} and recorded in its history.`,
    attachDialogPick: "Choose files",
    attachHelperNow:
      "You can choose several files, at once or one after another, and remove any before attaching.",
    attachDialogSubmit: (count: number): string =>
      count <= 1 ? "Attach" : `Attach ${String(count)} documents`,
    attachDialogBusy: "Attaching...",
    attachDialogRequired: "Choose at least one file to attach.",
    attachDialogFailed: (names: readonly string[]): string =>
      `These did not attach: ${names.join(", ")}. They are still listed: try again, or remove them.`,
    attachedToast: (count: number, target: string): string =>
      `${String(count)} document${count === 1 ? "" : "s"} attached to ${target}.`,
    documentsHeading: "Documents",
    documentsEmpty: "No documents are attached yet. Attach document, above, adds them.",
    documentsFailed: "The documents could not be read.",
    documentsColumnFile: "Document",
    documentsColumnAdded: "Added",
    /* The project team (wave W1.5, finding 5). */
    teamHeading: "Project team",
    teamLede:
      "Everyone assigned to this project or to its contracts. Assigning adds a person to the team; it never replaces anyone.",
    teamEmpty: "Nobody is assigned to this project yet.",
    teamColumnPerson: "Person",
    teamColumnRole: "Role",
    teamColumnScope: "Assigned to",
    teamColumnBy: "Assigned by",
    teamScopeProject: "The project",
    teamScopeContract: (contract: string): string =>
      contract === "" ? "A contract of this project" : `Contract ${contract}`,
    teamScopeAContract: "A contract of this project",
    teamAssignedBy: (who: string, when: string): string => (when === "" ? who : `${who}, ${when}`),
    teamAssignedBySeed: "Set up with the system",
    /* Wave W2 (Mahmoud's UAT): the contract page carries its team too. */
    contractTeamHeading: "Contract team",
    contractTeamLede:
      "Everyone assigned to this contract, and everyone assigned to its project (a project assignment covers its contracts). Assigning adds a person; it never replaces anyone.",
    contractTeamEmpty: "Nobody is assigned to this contract or its project yet.",
    historyHeading: "Project history",
    historyEmpty:
      "No tracked actions yet. Each registration, approval and assignment is recorded here as it happens.",
    historyFailed: "The project history could not be read.",
    retry: "Try again",
    someone: "Someone",
  },

  /* ------------------------------------------------------------------ */
  /* The program lens: one stage-scoped view over the work (M4).          */
  /* ------------------------------------------------------------------ */
  programs: {
    /* Each lens names what it covers under its heading (M5 P6 seat X #1); the four
     * program routes stopped being one identical screen under four titles. */
    lenses: {
      engineering:
        "The detailed-engineering stage's records: the engineering assessments and the inspection records and plans that feed them.",
      procurement:
        "The procurement and fabrication stage's records: the procurement surveillance of the contractor's and vendors' supply.",
      construction:
        "The construction and installation stage's records: the construction surveillance of the work on site.",
      externalAudits:
        "The audit records: planned audits of the management system and its processes, across the stages.",
    },
    lensEmpty:
      "No records of this stage's kinds are in view. Records appear here as their program plans them.",
    calendarHeading: "Program calendar",
    activitiesHeading: "Activity schedule",
    inspectionsHeading: "Inspection records",
    itpsHeading: "Inspection and test plans",
    requestOpen: "Request an inspection",
    requestsHeading: "Inspection requests",
    requestsEmpty:
      "No inspection request under this program yet. Send one to the contractor with a proposed date; the contractor agrees it or proposes another.",
    requestFromOperator: "Sent by the operator",
    requestFromContractor: "RFI from the contractor",
    requestHeading: (program: string): string => `Request an inspection: ${program}`,
    requestLede:
      "The request goes to the main contractor with the date you propose. It agrees the date or proposes another, the same loop as an RFI; the agreed date lands in both calendars and the inspection is conducted with this program's checklist.",
    requestContract: "Contract",
    requestDiscipline: "Discipline",
    requestScope: "Scope: what is to be inspected",
    requestDate: "Proposed date",
    requestLocation: "Location (optional)",
    requestAssignee: "Assignee (optional)",
    requestAssigneePlaceholder: "Select or name an assignee",
    requestAssigneeNone: "Nobody yet: named at the date agreement",
    requestAssigneeOption: (name: string, role: string): string => `${name} (${role})`,
    requestAssigneeHint:
      "Only the assignee starts the inspection. The people offered are the contract's team; the server says if one cannot conduct it.",
    requestAttachments: "Attachments (optional)",
    requestAttachmentsEmpty:
      "No files attached yet. You can attach drawings, specifications or scopes.",
    requestChoose: "Choose",
    requestMissing: (fields: readonly string[]): string =>
      `Still needed before sending: ${fields.join(", ")}.`,
    requestSend: "Send to the contractor",
    requestSent: (humanId: string): string =>
      `${humanId} sent. The contractor has a task to agree the date or propose another.`,
  },

  /* ------------------------------------------------------------------ */
  /* The stage/discipline matrix filter (Ruling 20). Two axes, never     */
  /* conflated; the options come from the taxonomies read model.         */
  /* ------------------------------------------------------------------ */
  matrix: {
    stageAxis: "Stage",
    disciplineAxis: "Discipline",
    allStages: "All stages",
    allDisciplines: "All disciplines",
    stageNotCarried: "These records do not carry a stage.",
    disciplineNotCarried: "These records do not carry a discipline.",
  },

  /* ------------------------------------------------------------------ */
  /* The persona bar (Ruling 17): real sign-out, real OIDC with a        */
  /* login_hint. No token is held, minted or swapped by the client.      */
  /* ------------------------------------------------------------------ */
  persona: {
    heading: "Demo personas",
    helper:
      "Switching signs you out and opens the identity provider's own sign-in page with the account preselected. Authentication stays real.",
    switchTo: (username: string): string => `Continue as ${username}`,
  },

  /* ------------------------------------------------------------------ */
  /* The chart kit (ADR-026, Recharts since Wave C).                    */
  /* ------------------------------------------------------------------ */
  charts: {
    emptyTitle: "Nothing to chart yet",
    emptyBody: "When the records arrive, the chart draws here.",
    /** The accessible name of one mark: "Piping: 3". */
    mark: (label: string, value: number): string => `${label}: ${String(value)}`,
    /** The accessible name of one stacked segment. */
    segment: (series: string, label: string, value: number): string =>
      `${series}, ${label}: ${String(value)}`,
    /** The accessible name of one line point. */
    point: (series: string, label: string, value: number): string =>
      `${series} at ${label}: ${String(value)}`,
    legend: "Legend",
    /** Axis labels (spec section 4: both axes labeled, what and unit). */
    categoryAxis: "Category",
    valueAxis: "Count",
    ageAxis: "Age",
    openItemsAxis: "Open items",
    toggleSeries: "Toggle series",
    /* The Wave C catalog's own axis names (spec section 4: what and unit). */
    monthAxis: "Month",
    monthUnit: "month",
    inspectionsAxis: "Inspections conducted",
    passRateAxis: "First-time pass rate (%)",
    positiveSeries: "Positive observations",
    negativeSeries: "Negative observations",
    contractorAxis: "Contractor",
    rootCauseAxis: "Findings",
    funnelStageAxis: "Stage",
    /** The accessible name of one diverging bar side: "March: 3 positive". */
    diverging: (label: string, value: number, side: string): string =>
      `${label}: ${String(value)} ${side}`,
  },

  /* ------------------------------------------------------------------ */
  /* The calendar (M4 Phase 3): month and quarter grids, legend, lanes.  */
  /* ------------------------------------------------------------------ */
  calendar: {
    heading: "Calendar",
    lede: "The inspection dates on the schedules and the RFIs, the unplanned candidates owed one, and the periods that shade them.",
    /* Wave W2: his layout (year first, then the picture of the registers, then the
       registers) and the calendar's own action. */
    visualHeading: "What the registers hold, month by month",
    visualLede: "Planned activities and RFIs in the year shown, by where their date stands.",
    visualMonthAxis: "Month",
    seriesAgreed: "Activities, date agreed",
    seriesWaiting: "Activities, waiting for an answer",
    seriesDraft: "Activities, not yet sent",
    seriesRfiAgreed: "RFIs, date agreed",
    seriesRfiWaiting: "RFIs, waiting for an answer",
    unplannedCount: (count: number): string =>
      count === 1
        ? "1 candidate is still owed a date."
        : `${String(count)} candidates are still owed a date.`,
    rfiRegisterHeading: "RFIs",
    rfiRegisterEmpty: "No RFI falls in this view.",
    planActivity: "Plan an activity",
    planHeading: "Plan an activity",
    planLede:
      "Pick the project and the contract, the ITP item or the contractor, the inspection and the dates. It lands in the project's open draft schedule for that year.",
    planProjectLabel: "Project",
    planContractLabel: "Contract",
    planDisciplineLabel: "Discipline",
    planDisciplineAll: "Every discipline",
    /* Wave Experience A: the plan list between the category and the item. */
    planItpLabel: "Inspection and test plan",
    planItpAll: "Every plan on the contract",
    planCandidateLabel: "ITP item to plan",
    planCandidateNone: "None: an activity without an ITP item",
    planNeedsContract: "Pick the contract.",
    planSubmitLabel: "Send the schedule for approval now",
    planSubmitNotYours:
      "You plan here; the schedule's submission for approval is another person's step.",
    planDone: (activity: string, schedule: string, submitted: boolean): string =>
      submitted
        ? `${activity} planned in ${schedule}, and ${schedule} went for approval.`
        : `${activity} planned in ${schedule}, a draft. It goes for approval when the schedule is submitted.`,
    openSchedule: "Open the schedule",
    legendActivity: "Planned activity",
    legendRfi: "RFI",
    legendDraft: "Not yet sent",
    legendWaiting: "Waiting for an answer",
    legendAgreed: "Date agreed",
    legendDone: "Conducted or closed",
    legendCancelled: "Cancelled",
    /* Wave Experience A, item 4: the three states every calendar draws. */
    legendYours: "Waiting on you",
    legendOpen: "In progress",
    legendOver: "Done, closed or cancelled",
    categoryLabel: "Category",
    categoryAll: "Everything",
    categoryActivities: "Planned activities",
    categoryRfis: "RFIs and requests",
    addBlockedPeriod: "Add blocked period",
    blockedHeading: "Add a blocked period",
    blockedLede:
      "A shutdown, a holiday or an event that blocks or warns on the dates it covers. Planned dates inside it are checked against it.",
    blockedKind: "Kind",
    blockedName: "Name",
    blockedStarts: "Starts on",
    blockedEnds: "Ends on",
    blockedProject: "Applies to",
    blockedTenantWide: "Every project (company wide)",
    blockedNote: "Note (optional)",
    blockedSubmit: "Add the period",
    blockedAdded: (name: string): string => `${name} is on the calendar.`,
    quarterMonths: (names: readonly string[]): string => names.join(", "),
    monthView: "Month",
    quarterView: "Quarter",
    yearView: "Year",
    previous: "Previous",
    next: "Next",
    legendHeading: "Legend",
    /** The period shading, named in plain words. */
    shadeBlock: "Blocked period",
    shadeWarn: "Caution period",
    unplannedHeading: "Unplanned work",
    unplannedLede: "Candidates owed a date. Plan them from their inspection and test plan.",
    unplannedEmpty: "Nothing is waiting for a date.",
    listHeading: "Scheduled inspections",
    listEmpty: "Nothing scheduled in this view.",
    openRecord: "Open record",
    columnIdentifier: "Identifier",
    columnProject: "Project",
    columnContractor: "Contractor",
    columnDate: "Date",
    columnPriority: "Priority",
    columnStatus: "Status",
    assessors: "Assessors",
    loading: "Reading the calendar...",
    failed: "The calendar could not be read. Refresh to try again.",
    monthNames: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ] as readonly string[],
    /** Monday first, to match the grid. */
    weekdayNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as readonly string[],
    monthOf: (monthName: string, year: number): string => `${monthName} ${String(year)}`,
    quarterOf: (quarter: number, year: number): string => `Q${String(quarter)} ${String(year)}`,
    yearOf: (year: number): string => String(year),
    dayCount: (count: number): string =>
      count === 1 ? "1 inspection" : `${String(count)} inspections`,
  },

  /* ------------------------------------------------------------------ */
  /* Stations a later increment delivers. Named as future, never broken. */
  /* ------------------------------------------------------------------ */
  future: {
    calendarTitle: "The calendar arrives with the next increment",
    calendarBody:
      "The month and quarter grids over activities, planning candidates and calendar periods are being built now. The schedule they will show is live on the Schedules screens today.",
    lessonsTitle: "Lessons learned arrives with the next increment",
    lessonsBody:
      "Closed findings will be marked lesson candidates and captured here. The finding register carries the records the register will read.",
    evaluationsTitle: "My Evaluations arrives with the next increment",
    evaluationsBody:
      "The computed contractor dossier and the quality evaluation view are being built now. The contractor numbers already live in the Parties & Contractors register.",
  },

  /* ------------------------------------------------------------------ */
  /* The dashboard: persona compositions over the analytics read models. */
  /* Every number arrives from GET /api/analytics/portfolio, the dossier  */
  /* read model or the date-bounded feeds; nothing fans out list calls.   */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Home, the inbox (wave W1, ruling 4): my tasks, my queues, my next   */
  /* 14 days. The analytics live on the Dashboard, not here.             */
  /* ------------------------------------------------------------------ */
  home: {
    lede: "What is waiting for you: your projects first, then your tasks, the queues you work, and the next 14 days.",
    /* Wave Experience A, item 1: Home is the account's projects as cards. */
    projectsHeading: "My projects",
    projectsLede:
      "One card per project you work on: what waits on you there, what you were told, the next date and the quality numbers.",
    projectsEmptyTitle: "No project is open to you yet",
    projectsEmptyBody:
      "A project appears here once you are assigned to it or to one of its contracts.",
    cardWaiting: (count: number): string =>
      count === 1 ? "1 task waiting on you" : `${String(count)} tasks waiting on you`,
    cardNothingWaiting: "Nothing waiting on you",
    cardFyi: (count: number): string =>
      count === 1 ? "1 for your information" : `${String(count)} for your information`,
    cardNext: "Next date",
    cardNoNext: "No date planned ahead",
    cardFindingsOpen: "Open findings",
    cardPunchOpen: "Open punch items",
    cardInspectionsOpen: "Inspections in progress",
    cardRfisOpen: "Open RFIs",
    cardOpenBible: (humanId: string): string => `Open ${humanId}`,
    caughtUp: "You are all caught up. Nothing is waiting for you in your tasks or your queues.",
    tasksHeading: "My tasks",
    tasksLede: "The steps addressed to you or to your organisation. Each opens where it is done.",
    tasksEmptyTitle: "No tasks are waiting for you",
    tasksEmptyBody: "A task arrives here when a step in a workflow needs you.",
    allTasks: (open: number): string =>
      open > 0 ? `All tasks and inbox (${String(open)} open)` : "All tasks and inbox",
    queuesHeading: "My queues",
    queuesLede:
      "The records waiting on you, counted the way the queue page lists them. Open a queue to work it.",
    queuesNone: "Your role works from its tasks; it has no queues of its own.",
    next14Heading: "My next 14 days",
    next14Lede: "The planned inspection dates from today to two weeks ahead.",
    next14EmptyTitle: "Nothing is planned in the next 14 days",
    next14EmptyBody: "Planned dates appear here once a schedule carrying them is distributed.",
    openCalendar: "Open the calendar",
    openDocket: "Open the witness docket",
    columnActivity: "Activity",
    columnDate: "Date",
    columnType: "Inspection",
    columnContractor: "Contractor",
    columnState: "State",
    loadFailed: "This part of your Home could not be read. Reload the page to try again.",
  },

  /* The queues (wave W1, R13). The count on Home and the list on the queue page come
     from ONE read, so a queue can never count one number and show another. */
  queues: {
    open: (count: number): string => `Open the queue (${String(count)})`,
    emptyOpen: "Open the register",
    more: (count: number): string => `and ${String(count)} more`,
    itpReview: {
      label: "Plans waiting for your review",
      empty: "No plan is waiting for your review.",
    },
    scheduleSign: {
      label: "Schedules waiting for your signature",
      empty: "No schedule is waiting for your endorsement or approval.",
    },
    resultApproval: {
      label: "Results waiting for your approval",
      empty: "No inspection result is waiting for your approval.",
      watch: (count: number): string =>
        count === 1
          ? "1 result is at final approval in all, waiting on whoever may approve it."
          : `${String(count)} results are at final approval in all, waiting on whoever may approve them.`,
      watchLink: "See them",
    },
    rectifications: {
      label: "Rectifications to verify",
      empty: "No rectification is waiting to be verified.",
    },
    responsesToDecide: {
      label: "Responses waiting for a decision",
      empty: "No proposed response is waiting for a decision.",
    },
    myInspections: {
      label: "Your open inspections",
      empty: "No open inspection is assigned to you.",
    },
    openFindings: {
      label: "Open findings on your work",
      empty: "No finding on your work is open.",
    },
    responseStep: {
      label: "Findings in the response step",
      empty: "No finding is waiting on a response or on the decision about one.",
    },
    rfiQueue: {
      label: "Inspection requests (RFIs)",
      empty: "No inspection request is waiting on you.",
      emptyBody: "When an inspection request is submitted or assigned to you, it appears here.",
      /* Ruling B: the contractor's card lists only the requests whose next move is its own. */
      yourMove: "Your move",
      toReview: "To review",
      toReviewEmpty: "No request waiting for your review.",
      assignedToMe: "Assigned to me",
      assignedToMeEmpty: "No request assigned to you.",
    },
    assignedInspections: "Inspections assigned to you",
    /* The banner a register shows while it is a queue. */
    banner: (count: number, label: string): string => `${label}: ${String(count)}.`,
    bannerLede: "Only the records waiting on you are listed.",
    showAll: "Show the whole register",
  },

  /* The task kinds, in plain words (wave W1: every station raises a task). */
  tasks: {
    /* Wave W2: the list narrowed to one record ("View open tasks" on a record page). */
    recordBanner: (label: string): string => `Your tasks on ${label} only.`,
    recordBannerLede: "Tasks about this record and the records under it.",
    recordShowAll: "Show all my tasks",
    recordUnnamed: "this record",
    heading: "Tasks and inbox",
    lede: "What is yours to do, and the handoffs waiting for your attention.",
    openAction: "Open the action",
    complete: "Complete",
    completing: "Completing...",
    completed: "Task marked complete.",
    completeFailed: "Could not complete the task. Please try again.",
    done: "Done",
    addressedToParty: "Addressed to your organisation",
    closesThroughAction: "Closes by itself when you take the action.",
    /* Wave COM item 1: the due time from the tenant's task clock, the scheduler's mark. */
    dueColumn: "Due",
    dueOn: (when: string): string => `Due ${when}`,
    noDue: "No due time",
    overdue: "Overdue",
    overdueSince: (when: string): string => `Marked overdue ${when}`,
    orderLabel: "Order",
    orderNewest: "Newest first",
    orderDue: "Due soonest",
    dueFilterLabel: "Due",
    dueFilterAny: "Any",
    dueFilterOverdue: "Overdue only",
    dueFilterDated: "With a due time",
    dueEmptyTitle: "No task matches this due filter",
    dueEmptyBody: "Choose Any to see every task again.",
    /* Wave COM item 2: the two scheduler kinds, each with its station in words. */
    stations: {
      ESCALATION:
        "Escalated to you: the task it follows is past its due time. Open the record to follow it up.",
      REVIEW_EFFECTIVENESS:
        "The closed action request's effectiveness review is due. Open it to record the outcome.",
    } as Readonly<Record<string, string>>,
    viewOverdueRecord: "View the overdue record",
    viewActionRequest: "View action request",
    kinds: {
      REGISTER_CONTRACT: "Register contract",
      AWARD_CONTRACT: "Contract award (no longer needed)",
      APPROVE_PROJECT: "Approve project",
      ASSIGN_PERSONNEL: "Assign project personnel",
      ASSIGN_QC_ENGINEER: "Assign QC engineer",
      AUTHOR_ITPS: "Author inspection and test plans",
      REVIEW_ITPS: "Review inspection and test plans",
      REVIEW_ITP: "Review a submitted plan",
      REVISE_ITP: "Revise a returned plan",
      ENDORSE_SCHEDULE: "Endorse a schedule",
      APPROVE_SCHEDULE: "Approve a schedule",
      ACKNOWLEDGE_SCHEDULE: "Acknowledge a distributed schedule",
      RESPOND_FINDING: "Respond to a finding",
      VERIFY_FINDING_RESPONSE: "Decide on a proposed response",
      IMPLEMENT_FINDING: "Declare an agreed correction implemented",
      VERIFY_FINDING_IMPLEMENTATION: "Verify an implemented correction",
      ANSWER_DATE_PROPOSAL: "Answer a proposed date",
      REVIEW_RFI: "Answer an inspection request",
      ANSWER_RFI_DATE: "Answer a proposed RFI date",
      CONDUCT_RFI: "Attend an agreed inspection",
      STAFF_PROJECT: "Assign a QA/QC engineer to a project",
      AUTHORITY_REVIEW: "Complete or return a proposal as the engineering authority",
      CONCUR_ITEM_COMPLETION: "Concur that a declared item is complete",
      ESCALATION: "Follow up an overdue task escalated to you",
      REVIEW_EFFECTIVENESS: "Record whether a closed action was effective",
    } as Record<string, string>,
  },

  /* Wave b12 frontend item 3 (wave COM item 4): the account page and its preferences. */
  account: {
    heading: "Your account",
    lede: "Who you are signed in as, and how Qualify reaches you.",
    identityHeading: "Signed in as",
    nameLabel: "Name",
    usernameLabel: "Login name",
    emailLabel: "Email",
    noEmail: "No email address on this account",
    preferencesHeading: "Preferences",
    emailOptOutLabel: "Send me no email",
    emailOptOutHint:
      "The in-app bell and your task list stay. An account email (a password sent to you, for example) is still sent.",
    emailOn: "Qualify emails you about your tasks and the records you follow.",
    emailOff: "Qualify sends you no email. The in-app bell stays.",
    save: "Save preferences",
    saving: "Saving...",
    saved: "Preferences saved.",
    unchanged: "Nothing changed: your preferences already read this.",
  },

  /* Wave b12 frontend item 4 (wave COM items 4 and 5): the power user's outboxes. */
  outbox: {
    heading: "Outbox",
    lede: "Every email and webhook call the tenant has queued, newest first, and what became of it.",
    emailTab: "Email",
    webhookTab: "Webhooks",
    statusFilterLabel: "Status",
    allStatuses: "All",
    statusCount: (label: string, count: number): string => `${label} (${String(count)})`,
    emailCaption: "The email outbox",
    webhookCaption: "The webhook outbox",
    emptyTitle: "Nothing in this outbox",
    emptyBody: "Rows appear here as tasks open, bells ring and records move.",
    emptyFilteredTitle: "No row has this status",
    emptyFilteredBody: "Choose All to see every row again.",
    columnCreated: "Queued",
    columnRecipient: "Recipient",
    columnMessage: "Message",
    columnStatus: "Status",
    columnAttempts: "Attempts",
    columnWhen: "Sent or next attempt",
    columnDeliveredWhen: "Delivered or next attempt",
    columnWhy: "Why",
    columnEndpoint: "Endpoint",
    columnEvent: "Event",
    sentOn: (at: string): string => `Sent ${at}`,
    deliveredOn: (at: string): string => `Delivered ${at}`,
    nextAttempt: (at: string): string => `Next attempt ${at}`,
    noNextAttempt: "Not retried",
    statusCode: (code: number): string => `The endpoint answered ${String(code)}.`,
    emailStatus: {
      PENDING: "Waiting to send",
      SENT: "Sent",
      SUPPRESSED: "Not sent",
      FAILED: "Failed",
    } as Readonly<Record<string, string>>,
    webhookStatus: {
      PENDING: "Waiting to deliver",
      DELIVERED: "Delivered",
      DEAD: "Given up",
      FAILED: "Failed",
    } as Readonly<Record<string, string>>,
    suppression: {
      NO_EMAIL: "The person has no email address.",
      OPTED_OUT: "The person opted out of email.",
      DUPLICATE_OF_TASK: "The task's own email already went to this person.",
      SECRET_EXPIRED:
        "The one-time password was no longer held when the email was due; reset it instead.",
    } as Readonly<Record<string, string>>,
  },

  /* Wave b12 frontend item 8 (wave STD rulings 8 to 14): the standards library. */
  standards: {
    heading: "Standards library",
    lede: "The standards and owner specifications your records cite, their editions and their clauses. A citation names one clause of one edition; when an edition is superseded, the edition change moves its citations across.",
    libraryTab: "Library",
    citationsTab: "Where cited",
    standardsCaption: "The standards",
    columnCode: "Code",
    columnTitle: "Title",
    columnBody: "Body",
    columnKind: "Kind",
    columnCurrent: "Edition in force",
    columnEditions: "Editions",
    kindStandard: "Standard",
    kindOwnerSpec: "Owner specification",
    productRow: "Shipped with Qualify",
    tenantRow: "Your own",
    noCurrent: "None in force",
    open: "Open",
    emptyTitle: "No standard in the library",
    emptyBody: "Standards appear here once the library is loaded for this tenant.",
    editionsHeading: (code: string): string => `Editions of ${code}`,
    editionsCaption: "The editions",
    columnEdition: "Edition",
    columnStatus: "Status",
    columnPublished: "Published",
    columnSuccessor: "Superseded by",
    columnTransition: "Transition ends",
    approximate: (on: string): string => `about ${on}`,
    approximateTitle: "The publication date is known only roughly.",
    noDate: "Not recorded",
    noSuccessor: "",
    status: {
      CURRENT: "In force",
      SUPERSEDED: "Superseded",
      WITHDRAWN: "Withdrawn",
    } as Readonly<Record<string, string>>,
    clauses: "Clauses",
    clausesHeading: (edition: string): string => `Clauses of ${edition}`,
    clausesEmpty: "No clause is recorded for this edition.",
    editionChange: "Move citations to this edition",
    editionChangeHeading: (edition: string): string => `Edition change to ${edition}`,
    editionChangeLede:
      "A dry run first: nothing is written. Each citation of a superseded edition's clause is moved through the clause map; one that maps to a single clause moves, the rest are listed for your review.",
    dryRunning: "Reading what would change...",
    countsHeading: "What the change does",
    count: {
      same: "Move to the mapped clause",
      folded: "Folded into an existing citation",
      split: "Split: review",
      merged: "Merged: review",
      dropped: "Dropped: review",
      unmapped: "No map row: review",
      new: "New clauses in this edition",
    } as Readonly<Record<string, string>>,
    reviewHeading: "For your review",
    reviewEmpty: "Nothing to review: every citation maps to one clause.",
    reviewCaption: "Citations to review",
    columnRelation: "Relation",
    columnRecord: "Record",
    columnFrom: "From",
    columnTo: "To",
    relation: {
      SAME: "Same",
      SPLIT: "Split",
      MERGED: "Merged",
      DROPPED: "Dropped",
      UNMAPPED: "Not mapped",
      NEW: "New clause",
    } as Readonly<Record<string, string>>,
    execute: "Move the citations",
    executed: (moved: number): string =>
      moved === 0
        ? "Nothing was moved: no citation maps to one clause. Nothing was written."
        : `${String(moved)} citation${moved === 1 ? "" : "s"} moved to this edition.`,
    target: {
      CHECKLIST_BANK_LINE: "Checklist line",
      ITP_ITEM: "ITP item",
      VERIFICATION_TYPE: "Verification type",
      DECISION_RULE: "Decision rule",
      QC_PROGRAMME_ROW: "QC programme row",
    } as Readonly<Record<string, string>>,
    // The citation chips and the picker, the same on the five registers.
    citationsLabel: "Cited clauses",
    noCitations: "No clause cited",
    chip: (code: string, edition: string, clause: string): string => `${code}:${edition} ${clause}`,
    superseded: "Superseded edition",
    supersededTitle:
      "This edition is superseded. The edition change moves the citation to the edition in force.",
    remove: (chip: string): string => `Remove the citation ${chip}`,
    removed: "Citation removed.",
    cite: "Cite a clause",
    citeHeading: (record: string): string => `Cite a clause from ${record}`,
    citeStandard: "Standard",
    citeStandardChoose: "Select a standard",
    citeEdition: "Edition",
    citeEditionChoose: "Select an edition",
    citeEditionOption: (label: string, status: string): string => `${label} (${status})`,
    citeClause: "Clause",
    citeClauseChoose: "Select a clause",
    citeSend: "Cite the clause",
    cited: (chip: string): string => `${chip} cited.`,
    refusalHeading: "The citation was not changed",
    // The registers of the Where cited tab.
    decisionRulesHeading: "Decision rules",
    decisionRulesEmpty: "No decision rule is configured.",
    banksHeading: "Checklist bank lines",
    bankChoose: "Select a checklist bank",
    bankLabel: "Checklist bank",
    banksEmpty: "No checklist bank is configured.",
    programmesHeading: "QC programme rows",
    programmeChoose: "Select a QC programme",
    programmeLabel: "QC programme",
    programmesEmpty: "No QC programme is configured.",
    rowsEmpty: "No row.",
    columnRow: "Row",
    columnDetail: "What it says",
    columnCitations: "Cited clauses",
    verificationTypesNote:
      "Verification types are cited on the Configuration page; ITP items on their plan's page.",
  },

  /* Wave b12 frontend item 9 (wave CRED rulings 15 to 21): credentials and evidence. */
  credentials: {
    heading: "Credentials",
    lede: "Qualifications of people, approvals of suppliers and calibrations of instruments, each with its validity and evidence. They are checked at the moment of use: an inspector without a valid qualification cannot start, a supplier without an approval cannot be awarded, an instrument out of calibration measures nothing.",
    caption: "The credential register",
    kindFilterLabel: "Holder",
    kindAll: "All",
    kind: {
      PERSON: "People",
      SUPPLIER: "Suppliers",
      INSTRUMENT: "Instruments",
    } as Readonly<Record<string, string>>,
    holderFilterLabel: "Holder",
    holderAll: "Every holder",
    columnType: "Credential",
    columnHolder: "Holder",
    columnReference: "Reference",
    columnIssued: "Issued",
    columnValidUntil: "Valid until",
    columnIssuer: "Issuer",
    columnStatus: "Status",
    columnEvidence: "Evidence",
    noExpiry: "No expiry",
    noEvidence: "None",
    evidenceFile: (index: number): string => `Evidence ${String(index)}`,
    status: {
      VALID: "Valid",
      EXPIRED: "Expired",
      SUSPENDED: "Suspended",
      REVOKED: "Revoked",
    } as Readonly<Record<string, string>>,
    expiredTitle: "Past its validity date: read as expired, never stored.",
    emptyTitle: "No credential recorded",
    emptyBody: "Record the qualifications, approvals and calibrations the work relies on.",
    emptyFilteredTitle: "No credential for this holder",
    emptyFilteredBody: "Choose All to see every credential again.",
    record: "Record a credential",
    recordHeading: "Record a credential",
    typeLabel: "Credential type",
    typeChoose: "Select a type",
    personLabel: "Person",
    personChoose: "Select the person",
    personHint: "The people offered are those on the teams of the projects you can see.",
    partyLabel: "Supplier",
    partyChoose: "Select the supplier",
    instrumentLabel: "Instrument tag",
    referenceLabel: "Reference (certificate or approval number)",
    issuedLabel: "Issued on",
    validUntilLabel: "Valid until",
    validUntilRequired: "This type needs a validity date.",
    validUntilOptional: "Leave empty if it does not expire.",
    issuerLabel: "Issuer (optional)",
    evidenceLabel: "Evidence",
    evidenceRequired: "This type needs its evidence file.",
    evidenceEmpty: "No evidence file chosen.",
    recordSend: "Record the credential",
    recorded: (reference: string): string => `${reference} recorded.`,
    suspend: "Suspend",
    revoke: "Revoke",
    restore: "Restore",
    statusHeading: (act: string, reference: string): string => `${act}: ${reference}`,
    statusLede: {
      SUSPENDED: "A suspended credential is not valid until restored. Say why.",
      REVOKED: "A revoked credential is never restored; a new one is recorded instead. Say why.",
      VALID: "The credential is valid again from now, within its validity date.",
    } as Readonly<Record<string, string>>,
    reasonLabel: "Reason",
    reasonOptional: "Reason (optional)",
    reasonNeeded: "Say why first.",
    statusSend: (act: string): string => act,
    statusDone: (reference: string, status: string): string => `${reference}: ${status}.`,
    impact: "Where it measured",
    impactHeading: (tag: string): string => `Inspections measured with ${tag}`,
    impactLede:
      "Every inspection this instrument took a measured value on. If its calibration is lost, these are the records to look at again.",
    impactEmpty: "This instrument has measured nothing yet.",
    impactCaption: "Inspections measured with the instrument",
    impactColumnRecord: "Inspection",
    impactColumnState: "State",
    impactColumnCount: "Measurements",
    impactColumnFirst: "First measured",
    refusalHeading: "The credential was not changed",
    // The conduct sheet: the measured value of a line (ruling 17).
    measurementHeading: "Measured value",
    valueLabel: "Value",
    unitLabel: "Unit",
    toleranceMinLabel: "Tolerance from",
    toleranceMaxLabel: "Tolerance to",
    instrumentPickLabel: "Instrument",
    instrumentNone: "No instrument",
    instrumentOption: (tag: string, until: string | null): string =>
      until === null ? tag : `${tag} (calibrated until ${until})`,
    measurementSave: "Record the value",
    measurementSaved: "Value recorded.",
    within: "Within tolerance",
    outside: "Out of tolerance",
    outsideHint: "A line whose value is out of tolerance cannot be marked Pass.",
    measuredAs: (value: string, unit: string, min: string | null, max: string | null): string =>
      `${value} ${unit}${
        min !== null && max !== null
          ? ` (tolerance ${min} to ${max})`
          : min !== null
            ? ` (tolerance: at least ${min})`
            : max !== null
              ? ` (tolerance: at most ${max})`
              : ""
      }`,
    measurementNeeded: "Say the value and its unit.",
    refusalMeasurementHeading: "The value was not recorded",
    refusalPassHeading: "A line out of tolerance cannot pass",
    // The EN 10204 classification of a file (ruling 19).
    classify: "Classify",
    classifyHeading: (file: string): string => `Classify ${file}`,
    classifyLede:
      "Say which inspection document this file is. A 3.2 certificate names the inspection body that validated it.",
    documentTypeLabel: "Document type",
    documentType: {
      EN10204_2_1: "EN 10204 2.1 declaration of compliance",
      EN10204_2_2: "EN 10204 2.2 test report",
      EN10204_3_1: "EN 10204 3.1 inspection certificate",
      EN10204_3_2: "EN 10204 3.2 inspection certificate",
      OTHER: "Another document",
    } as Readonly<Record<string, string>>,
    heatLabel: "Heat or lot (optional)",
    bodyLabel: "Inspection body",
    bodyChoose: "Select the inspection body",
    bodyNeeded: "A 3.2 certificate names its inspection body.",
    classifySend: "Save the classification",
    classified: (file: string): string => `${file} classified.`,
    classification: (type: string, heat: string | null): string =>
      heat === null ? type : `${type} · heat or lot ${heat}`,
    refusalEvidenceHeading: "The classification was not saved",
    // The staffing panel on a contract (ruling 20).
    staffingHeading: "Staffing",
    staffingLede:
      "The qualified people this contract needs, per role and discipline, and how many it has now.",
    staffingCaption: "The contract's staffing rules",
    staffingEmpty: "No staffing rule on this contract.",
    staffingColumnRole: "Role",
    staffingColumnDiscipline: "Discipline",
    staffingColumnNeeded: "Needed",
    staffingColumnQualified: "Qualified",
    staffingColumnShortfall: "Short by",
    staffingEveryDiscipline: "Every discipline",
    staffingMet: "Met",
    staffingShort: (count: number): string => `${String(count)} short`,
    enforcedOff: "switched off until the inspectors' qualifications are recorded",
    enforcedOn: "Enforced at the moment of use",
    staffingAdd: "Add a rule",
    staffingAddHeading: "Add a staffing rule",
    staffingRoleLabel: "Role",
    staffingRoleChoose: "Select the role",
    staffingRoleHint: "The roles offered are those on the teams of the projects you can see.",
    staffingDisciplineLabel: "Discipline",
    staffingCountLabel: "How many qualified people",
    staffingCountHint: "1 to 500.",
    staffingSend: "Add the rule",
    staffingAdded: "Staffing rule added.",
    refusalStaffingHeading: "The rule was not added",
  },

  /* Wave b12 frontend item 12: a register narrowed to one project from its Home card. */
  projectFilter: {
    unnamed: "this project",
    banner: (view: string, project: string): string => `${view} of ${project} only.`,
    showAll: "Show the whole register",
    figureLink: (label: string, value: number, project: string): string =>
      `${label}: ${String(value)}. Open them on the register, ${project} only.`,
  },

  /* Wave b12 frontend items 4 and 6: what a webhook endpoint may subscribe to. */
  webhookEvents: {
    TASK_OPENED: "A task was opened",
    TASK_CLOSED: "A task was closed",
    BELL: "A notification was written",
    RECORD_STATE_CHANGED: "A record moved through its workflow",
  } as Readonly<Record<string, string>>,

  /* The witness docket (wave W1, ruling 6): the third party inspector's own page. */
  docket: {
    heading: "Witness docket",
    lede: "The planned inspection dates on your agency's contracts, from today, nearest first. Attend the ones that need your witness.",
    emptyTitle: "No inspection is planned ahead",
    emptyBody: "Dates appear here once a schedule on your agency's contracts is distributed.",
    horizon: (days: number): string => `The next ${String(days)} days`,
  },

  dashboard: {
    /* The executive quality risk alert (wave W1.5, finding 6): every contractor it names
     * links to its dossier and says, in plain words, which rule put it there. */
    riskAlertHeading: "Executive quality risk alert",
    riskAlertRule: (threshold: number): string =>
      `A contractor is listed here when its first-time pass rate is below ${String(threshold)}%, or when one of its corrective actions is past its due date.`,
    riskAlertCount: (count: number): string =>
      `${String(count)} contractor${count === 1 ? " needs" : "s need"} attention:`,
    riskReasonPassRate: (rate: number, threshold: number): string =>
      `first-time pass rate ${String(Math.round(rate))}%, below the ${String(threshold)}% threshold`,
    riskReasonOverdue: (count: number): string =>
      `${String(count)} corrective action${count === 1 ? " is" : "s are"} past ${count === 1 ? "its" : "their"} due date`,
    riskReasonOnlyOverdue: (rate: number, count: number): string =>
      `its first-time pass rate of ${String(Math.round(rate))}% meets the threshold, so it is listed only for the overdue action${count === 1 ? "" : "s"}`,
    riskOpenDossier: (party: string): string => `Open the dossier of ${party}`,
    riskHealthyHeading: "Portfolio quality health: normal",
    riskHealthyBody: (threshold: number): string =>
      `Every contractor meets the ${String(threshold)}% first-time pass rate threshold and has no corrective action past its due date.`,
    heading: "Dashboard",
    lede: "Your live view of the work. Every number is read from the system for this session; what you cannot see, the system has already hidden.",
    tileTotalInspections: {
      label: "Inspections conducted",
      empty: "No inspections conducted in the window yet.",
      action: "Open inspections",
    },
    tileNegative: {
      label: "Negative observations",
      empty: "No negative observations on record.",
      action: "Open negative observations",
    },
    tilePositive: {
      label: "Positive observations",
      empty: "No positive observations on record yet.",
      action: "Open positive observations",
    },
    tileOpenFindings: {
      label: "Open findings",
      empty: "No open findings. Findings are born on inspection lines when an outcome needs one.",
      action: "View open findings",
    },
    tileResponsesDue: {
      label: "Responses to agree",
      empty: "No proposed responses are waiting for a decision.",
      action: "Open findings with responses to agree",
    },
    tileAwaitingVerification: {
      label: "Findings awaiting verification",
      empty: "Nothing is waiting for verification.",
      action: "Open findings awaiting verification",
    },
    tileItpsSubmitted: {
      label: "Plans submitted for review",
      empty: "No plans are waiting for review.",
      action: "Open plans",
    },
    tileSchedulesToApprove: {
      label: "Schedules to endorse or approve",
      empty: "No schedules are waiting in the queue.",
      action: "Open schedules",
    },
    tileResultsFinal: {
      label: "Results at final approval",
      empty: "No results are waiting at final approval.",
      action: "Open inspection results at final approval",
    },
    trendHeading: "Findings raised versus closed",
    trendCaption: "The six monthly buckets the analytics model returns.",
    qualityHeading: "Quality per month",
    qualityCaption:
      "Inspections conducted each month. The first-time pass rate line needs a monthly series the read model does not publish yet, so only the bars draw.",
    divergingHeading: "Observations per month",
    divergingCaption:
      "Positive observations above the line, negative below, counted from the findings register.",
    rootCauseHeading: "Findings by root cause",
    rootCauseCaption: "The Ishikawa 6M distribution the analytics model publishes.",
    sparkAria: (label: string): string => `${label}: the 30-day trend`,
    trendPairHeading: "The most recent findings",
    viewAll: "View all",
    raisedSeries: "Findings raised",
    closedSeries: "Findings closed",
    conductedSeries: "Inspections conducted",
    contractorStatsHeading: "Contractor dossiers",
    contractorStatsLede:
      "The computed numbers per evaluable party: percentages only, exactly as the read model returns them.",
    columnParty: "Contractor",
    columnInspections: "Inspections conducted",
    columnPassRate: "First-time pass rate",
    columnTimeliness: "Response timeliness",
    columnMeanOpen: "Mean days open",
    columnOverdue: "Overdue actions",
    openItemsHeading: "Open items per contractor",
    openItemsFindings: "Open findings",
    openItemsActions: "Open actions",
    queueHeading: "The approval queue",
    queueLede: "The states the review loops leave waiting, counted by the analytics model.",
    tileWitnessNext14: {
      label: "Witness points in the next 14 days",
      empty: "No witness points in the next 14 days.",
      action: "Open the witness docket",
    },
    tileTpiOpen: {
      label: "Your open inspections",
      empty: "No open inspection is assigned to you.",
      action: "Open your inspections",
    },
    next14Heading: "The next 14 days",
    next14Lede: "The inspection dates inside the coming fortnight, as the feed answers for you.",
    next14Empty: "Nothing is scheduled in the coming fortnight.",
    recentHeading: "Recent activity",
    recentLede: "The dates just past, from the same bounded feed.",
    recentEmpty: "Nothing was scheduled in the past month.",
    myWorkHeading: "My Work",
    myWorkLede: "The records assigned to you, with anything past its date flagged.",
    myWorkEmpty: "Nothing is assigned to you right now.",
    overdueBadge: "Past its date",
    myItpsHeading: "Inspection and test plans by state",
    myItpsEmpty: "No plans are in your view yet.",
    myCalendarHeading: "My calendar",
    myCalendarEmpty: "No assigned dates this month. The full calendar is on the Calendar page.",
    percent: (value: number): string => `${String(value)}%`,
    days: (value: number): string => `${String(value)} days`,
    guideCard: {
      label: "First time here?",
      body: "The walkthrough takes both of you from sign-in to a closed finding, one step at a time. It is the same page the test guide in the handbook walks.",
      action: "Open the walkthrough",
    },
    loading: "Reading your numbers...",
    failed: "The dashboard could not be read. Refresh to try again.",
    errorDetail: "One of the reads could not be completed.",
  },

  /* ------------------------------------------------------------------ */
  /* The quality evaluation screen (Intelligence): per-contractor        */
  /* dossiers, percentages only. No band or rating logic (ADR-014 is a    */
  /* future station and no score is invented here).                       */
  /* ------------------------------------------------------------------ */
  evaluations: {
    heading: "Quality Evaluation",
    lede: "The response statistics per contractor, computed by the analytics read model. Percentages only.",
    commitmentHeading: "Level of commitment",
    commitmentBody: (rate: number): string =>
      `${String(rate)}% of decided responses were agreed by the target date.`,
    commitmentAbsent: "No responses have been decided yet, so there is no timeliness to show.",
    findingsByClassHeading: "Findings by class",
    agingHeading: "Open NCR and CAR aging",
    agingEmpty: "No open NCRs or CARs.",
    dossierEmpty: "No evaluable parties are in your view yet.",
    openActions: "Overdue actions",
    meanOpen: "Mean days open of closed findings",
    passRate: "First-time pass rate",
    inspections: "Inspections conducted",
    loading: "Reading the dossiers...",
    failed: "The evaluations could not be read. Refresh to try again.",
    myEvaluationsLede: "Your own dossier row, computed from your records. Percentages only.",
    divergingHeading: "Observations, positive against negative",
    divergingCaption:
      "The class distribution of this contractor's findings, positive above the line and negative below.",
    funnelHeading: "The response loop",
    funnelCaption:
      "This contractor's findings at each stage of the loop: raised, answered, verified, closed.",
    funnelStageRaised: "Finding raised",
    funnelStageAnswered: "Response agreed",
    funnelStageVerified: "Verified",
    funnelStageClosed: "Closed",
    radarHeading: "The five dossier metrics",
    radarCaption:
      "The dossier's own numbers side by side: inspections, pass rate, timeliness, mean days open and overdue actions. The axes carry different units, so read the spoke labels.",
  },

  /* ------------------------------------------------------------------ */
  /* Lessons learned (Ruling 18): the register and the marking flow.     */
  /* ------------------------------------------------------------------ */
  lessons: {
    heading: "Quality Lessons Learned",
    lede: "The findings marked as lessons: candidates first, then captured. Marking happens on a closed finding, operator side only; the system refuses everyone else.",
    filterStatus: "Lesson status",
    all: "All",
    columnFinding: "Finding",
    columnClass: "Class",
    columnStatus: "Lesson status",
    columnObserved: "Observed",
    columnNote: "Latest note",
    emptyTitle: "No lessons here yet",
    emptyBody:
      "A closed finding appears here once it is marked a lesson candidate from its own page. Nothing is marked yet for this filter.",
    statusNone: "Not marked",
    statusCANDIDATE: "Lesson candidate",
    statusCAPTURED: "Captured",
    markHeading: "Lessons learned",
    markLabel: "Mark as",
    markCandidate: "Lesson candidate",
    markCaptured: "Captured",
    noteLabel: "Note",
    markButton: "Record lesson status",
    markSuccess: "The lesson status was recorded on the finding's transition log.",
    registerHint:
      "Marking lives on the finding's own page, on a closed finding. The register reads what was marked.",
  },

  /* ------------------------------------------------------------------ */
  /* Shared register furniture.                                          */
  /* ------------------------------------------------------------------ */
  common: {
    loading: "Loading...",
    failed: "This could not be loaded. Refresh to try again.",
    empty: "Nothing here yet.",
    // Wave S: a counterparty account with no contract assigned reads nothing anywhere.
    noAssignmentTitle: "No contracts are assigned to your account yet.",
    noAssignmentBody: "Ask the operator's quality team to assign you to a contract.",
    total: (count: number): string => `${String(count)} in total`,
    page: (current: number, pages: number): string => `Page ${String(current)} of ${String(pages)}`,
    previousPage: "Previous page",
    nextPage: "Next page",
    humanId: "Identifier",
    status: "Status",
    party: "Party",
    date: "Date",
    comment: "Comment",
    comments: "Comments",
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    confirm: "Confirm",
    recordedBy: (who: string, when: string): string => `${who}, ${when}`,
    absenceHeading: "Not part of your view",
    absenceBody:
      "This screen is not part of your view of the system. If you believe you should be able to reach it, speak to your quality lead.",
    notFoundBody:
      "There is no record here for you. The system answers the same for a record that does not exist and one you may not see, and so does this screen.",
    backToDashboard: "Back to the dashboard",
    refusalHeading: "This move cannot happen yet",
    refusalJustificationHeading: "This move needs a justification",
    refusalJustificationBody: "Write a justification, then send the proposal",
    retry: "Try again",
    yes: "Yes",
    no: "No",
    searchLabel: "Search",
    searchNote: "Searches the records on this page.",
    validationHeading: "Some fields need fixing",
    // Wave W2: a refused field the open form has no control for is still named.
    unplacedField: (field: string, message: string): string =>
      `${field}: ${message} This form has no field for it; tell your system administrator.`,
    // Wave W1.5 (finding 2): a refused form names every field it refused, in plain words.
    validationSummary: (fields: readonly string[]): string =>
      fields.length === 1
        ? `Nothing was saved. Check this field: ${fields[0] ?? ""}.`
        : `Nothing was saved. Check these ${String(fields.length)} fields: ${fields.join("; ")}.`,
  },

  /* ------------------------------------------------------------------ */
  /* The plain names of the fields the server can refuse (wave W1.5,      */
  /* finding 2): the refusal summary names them, never their wire names.  */
  /* ------------------------------------------------------------------ */
  fieldNames: {
    interventions: "Intervention points",
    intervention_code: "Intervention level",
    intervening_party_type_id: "Intervening party",
    reference_document: "Reference document",
    verifying_document: "Verifying document",
    remarks: "Remarks",
    sequence: "Order in the plan",
    comment: "Comment",
    body: "Comment",
    note: "Note",
    status: "Status",
    title: "Title",
    description: "Description",
    reason: "Reason",
    action_type: "Action type",
    promotion_suggested: "Escalation suggestion",
    punch_list_id: "Punch list",
    completion_notes: "Completion notes",
    closure_reference: "Closure reference",
    closure_comments: "Closure comments",
    proposed_correction: "Correction",
    proposed_corrective_action: "Corrective action",
    proposed_target_date: "Target date",
    decision: "Decision",
    disposition_type: "Disposition",
    disposition_justification: "Justification",
    concession_reference: "Concession reference",
    root_cause_category: "Root cause",
    root_cause_analysis: "Root cause analysis",
    corrective_action_plan: "Corrective action plan",
    target_implementation_date: "Target date",
    evidence_items: "Evidence",
    closure_notes: "Closure notes",
    effectiveness_outcome: "Effectiveness outcome",
    effectiveness_notes: "Effectiveness notes",
    contract_id: "Contract",
    discipline_id: "Discipline",
    item_no: "Item number",
    activity_description: "Activity",
    inspection_method: "Inspection method",
    acceptance_criteria: "Acceptance criteria",
    planning_candidate_id: "Planning candidate",
    planned_date: "Planned date",
    verification_type_id: "Activity type",
    purpose: "Purpose",
    proposed_date: "Proposed date",
    justification: "Justification",
    scope: "Scope of the inspection",
    location: "Location",
    declaration_confirmed: "Readiness confirmation",
    inspector_person_ids: "Inspectors",
    itp_item_id: "ITP item",
    project_id: "Project",
    contractor_party_id: "Contractor",
    planned_end_date: "Last day",
    move: "Answer",
    outcome_value: "Outcome",
    comments: "Comments",
    // Wave INST: the instance, the structured declaration and the authority's acts.
    instance_label: "Instance",
    quantity: "Quantity",
    external_asset_id: "External asset id",
    internal_check_reference: "Internal check reference",
    attachment_ids: "Evidence",
    short_notice_justification: "Short-notice justification",
    declared_instance_count: "Number of instances",
    notice_lead_days: "Notice lead time",
    line_reference: "Equipment line",
  } as Record<string, string>,

  /* ------------------------------------------------------------------ */
  /* Choosing files (wave W1.5, finding 3): every pick appends, each     */
  /* file can be removed before anything is sent.                        */
  /* ------------------------------------------------------------------ */
  files: {
    none: "No files chosen.",
    remove: (name: string): string => `Remove ${name}`,
    count: (count: number): string => `${String(count)} files chosen.`,
    emptySkipped: (names: readonly string[]): string =>
      `${names.join(", ")} ${names.length === 1 ? "is" : "are"} empty and cannot be attached, so ${names.length === 1 ? "it was" : "they were"} not added.`,
  },

  /* ------------------------------------------------------------------ */
  /* The DataTable register furniture (foundation pilot, shadcn/ui +     */
  /* TanStack Table). Plain words; no rule codes.                        */
  /* ------------------------------------------------------------------ */
  dataTable: {
    searchPlaceholder: "Search this register",
    sortBy: (label: string): string => `Sort by ${label}`,
    filterBy: (label: string): string => `Filter ${label}`,
    filterActive: (count: number): string =>
      `${String(count)} filter value${count === 1 ? "" : "s"} chosen`,
    clearColumn: "Show every value",
    clearFilters: "Clear search and filters",
    shown: (shown: number, total: number): string => `${String(shown)} of ${String(total)} shown`,
    multiSortHint: "Shift-click a second column header to sort by it as well.",
    noMatches: "No record matches the search or the filters. Clear them to see every record.",
    notSet: "Not set",
    footer: (total: string, page: string): string => `${total} - ${page}`,
  },

  /* ------------------------------------------------------------------ */
  /* ITP register and the review loop.                                   */
  /* ------------------------------------------------------------------ */
  itps: {
    heading: "Inspection and test plans",
    lede: "The itemized plans the contractor registers and the operator reviews. An approved item becomes a planning candidate owed a date.",
    columnPlan: "Plan",
    columnContract: "Contract",
    columnDiscipline: "Discipline",
    columnContractor: "Contractor",
    columnItems: "Items",
    columnRevision: "Revision",
    columnState: "State",
    emptyTitle: "No inspection and test plans yet",
    emptyBody:
      "When a contractor registers a plan against one of your contracts it appears here, first as a draft. Nothing to review until then.",
    detailHeading: "Inspection and test plan",
    itemsHeading: "Items",
    reviewHeading: "Review exchange",
    historyHeading: "Phase history",
    itemNo: "No.",
    activity: "Activity",
    method: "Method",
    acceptance: "Acceptance criteria",
    itemStatus: "Item status",
    acceptedBadge: "Accepted",
    withdrawnBadge: "Withdrawn",
    candidateBadge: "Feeds planning",
    /* Wave W2: what an item's acceptance does to planning, said exactly. */
    planningLineHeading: "Planning",
    planningNotAttended: "No hold or witness point: nothing to plan.",
    planningRequestAgain: "Open for a new request after the rework. Last request",
    planningWaitsForAcceptanceOperator:
      "The operator attends this item. Accepting it makes it a planning candidate at once.",
    planningWaitsForAcceptanceContractor:
      "The operator attends this item. It becomes a planning candidate when the operator accepts it.",
    planningCandidate: (since: string): string =>
      `Planning candidate since ${since}: waiting for a date. The planner can plan it now, or the contractor requests it by RFI once the plan is approved.`,
    planningPlanned: "Planned as an activity:",
    planningRequested: "Requested by RFI:",
    planningAcceptedContractorWaits:
      "Accepted. You can request its inspection (RFI) once the whole plan is approved; the operator can already plan it.",
    planningAcceptedContractorReady: "Accepted. Request the inspection when the work is ready.",
    openPlanningCandidates: "Open planning candidates",
    candidateOpenedToast: (itemNo: string): string =>
      `Item ${itemNo} accepted. It is now a planning candidate, waiting for a date.`,
    candidateWithdrawnToast: (itemNo: string): string =>
      `Your objection to item ${itemNo} is recorded. It left the planning candidates.`,
    editAcceptance: "Edit acceptance",
    editAcceptanceHeading: (itemNo: string): string => `The acceptance of item ${itemNo}`,
    editAcceptanceLede:
      "The item stays accepted. Add information or files to the acceptance, then save. To take the acceptance back, object to the item instead.",
    editAcceptanceRecorded: "Recorded acceptance",
    editAcceptanceNoteLabel: "Add to the acceptance",
    editAcceptanceSave: "Save",
    editAcceptanceNeedsSomething: "Add a note or a file to save.",
    editAcceptanceSaved: "The acceptance was updated.",
    returnReasonLabel: "Reason for the return (sent to the contractor)",
    returnNeedsReason: "Write the reason for the return first.",
    returnObjectionsLede: (count: number): string =>
      count === 0
        ? "No item is objected to. The contractor receives your reason as a task."
        : `The contractor receives a task listing the ${String(count)} objected item${count === 1 ? "" : "s"} with your reasons:`,
    returnSent: "Returned. The contractor received a task with your objections.",
    raiseRfi: "Request inspection (RFI)",
    raiseRfiHeading: (itemNo: string): string => `Request the inspection of item ${itemNo}`,
    raiseRfiLede:
      "The operator accepts your date or proposes another. The agreed date lands in both calendars.",
    raiseFreeRfi: "Request inspection (RFI)",
    raiseFreeRfiHeading: "Request inspection under contract",
    raiseFreeRfiLede:
      "This is a free request under the contract, not tied to an ITP item. The operator accepts your date or proposes another.",
    freeRfiContractLabel: "Contract",
    freeRfiContractChoose: "Select a contract",
    freeRfiDisciplineLabel: "Discipline",
    freeRfiDisciplineChoose: "Select a discipline",
    freeRfiAttachmentsLabel: "Attachments (optional)",
    freeRfiPlainNotice: "This is a free request under the contract.",
    freeRfiAddFile: "Attach file",
    freeRfiRemoveFile: "Remove",
    rfiScopeLabel: "Scope of the inspection",
    rfiDateLabel: "Proposed date",
    rfiLocationLabel: "Location (optional)",
    rfiDeclaration: "I confirm the work is complete and ready for inspection.",
    rfiDeclarationNeeded: "Confirm the work is ready first.",
    rfiSend: "Send the request",
    rfiSent: (humanId: string): string => `${humanId} sent to the operator.`,
    // Wave INST ruling 1: the item is the template, the request names its instance.
    rfiInstanceLabel: "Instance",
    rfiInstanceLabelOptional: "Instance (optional)",
    rfiInstanceHint:
      "The item is a template: name the one instance this request covers, by its location, tag, lot or unit. Other instances of the item can be requested in parallel.",
    rfiInstanceHintOptional:
      "If the request covers one instance (a location, tag, lot or unit), name it.",
    rfiQuantityLabel: "Quantity",
    rfiQuantityHint: "How much of the instance this request covers. Leave 1 for one unit.",
    rfiAssetLabel: "External asset id (optional)",
    rfiAssetHint: "The asset's id in a completions tool, if the operator uses one.",
    // Wave INST ruling 3: the structured pre-inspection declaration.
    rfiDeclarationHeading: "Pre-inspection declaration",
    rfiCheckReferenceLabel: "Your internal check reference (optional)",
    rfiCheckReferenceHint: "The number of your own inspection record behind this declaration.",
    rfiEvidenceLabel: "Evidence of your own check (optional)",
    rfiEvidenceEmpty:
      "No evidence attached. Files attached here are linked to the request as evidence when it is sent.",
    rfiShortNoticeLabel: "Short-notice justification",
    rfiShortNoticeHint:
      "Say why the inspection cannot wait. The request is then sent flagged as short notice for the operator.",
    rfiSendShortNotice: "Send with the justification",
    rfiMissing: (fields: readonly string[]): string =>
      `Still needed before sending: ${fields.join(", ")}.`,
    rfiDeclarationMissing: "the readiness confirmation",
    refusalInstanceLabelHeading: "Name the instance this request covers",
    refusalAlreadyRequestedHeading: "This instance is already requested",
    refusalInstanceSettledHeading: "This instance is already cleared or waived",
    refusalShortNoticeHeading: "The proposed date is inside the notice lead time",
    refusalEvidenceUnknownHeading: "An evidence file was not found",
    raiseAnotherInstance: "Request another instance",
    // Wave RFI rulings 1 and 2 (b12 frontend item 7): plan, category, then the items.
    raiseRfiPlanHeading: (plan: string): string => `Request inspection on ${plan}`,
    citationRecord: (itemNo: string): string => `item ${itemNo}`,
    rfiPlanLabel: "Plan",
    rfiCategoryLabel: "Checklist category",
    rfiCategoryHint:
      "The program the inspection is conducted under; its checklist is used and its notice lead time applies.",
    rfiCategoryDefault: "The tenant's RFI type (default)",
    rfiItemsLegend: "Items of this request",
    rfiItemsHint: (max: number): string =>
      `Each item is its own instance. One request names at most ${String(max)} items; another instance of the same item is its own request.`,
    rfiItemLabel: "Item",
    rfiItemChoose: "Select an item",
    rfiItemOption: (itemNo: string, activity: string): string => `${itemNo} ${activity}`,
    rfiAddItem: "Add an item",
    rfiRemoveItem: "Remove",
    rfiItemsAtMost: (max: number): string =>
      `This request already names ${String(max)} items, the most one request can carry.`,
    rfiItemRow: (position: number): string => `Item ${String(position)}`,
    rfiMissingItem: (position: number): string => `Item (row ${String(position)})`,
    rfiMissingInstance: (position: number): string => `Instance (row ${String(position)})`,
    rfiItemTwice: "the same item named twice (another instance of an item is its own request)",
    refusalItemsHeading: "The items of this request need fixing",
    refusalCategoryHeading: "This checklist category is not available",
    refusalFreeEngineeringHeading: "A request without an ITP item is for engineering only",
    freeRfiCategoryHint:
      "Without an ITP item the operator accepts a request under an engineering category only, unless it has opened free requests to every category.",
    freeRfiEngineeringOnly:
      "Your operator accepts requests without an ITP item for engineering categories only.",
    // Wave INST ruling 2: the instances of an item and its completion in two hands.
    instancesLabel: "Instances of this item",
    instanceWholeItem: "Whole item",
    holdWaitsForConcurrence: (itemNo: string): string =>
      `Waits for hold point ${itemNo}: declared complete, waiting for the operator's concurrence.`,
    holdWaitsForDeclaration: (itemNo: string): string =>
      `Waits for hold point ${itemNo}: its instances are settled; the contractor declares it complete, then the operator concurs.`,
    holdWaitsForClearance: (itemNo: string): string =>
      `Waits for hold point ${itemNo}: its instances are cleared or waived, the contractor declares it complete, then the operator concurs.`,
    completionConcurred: (on: string): string =>
      `Concurred complete on ${on}. The item is cleared.`,
    completionDeclared: (declared: number, settled: number, on: string): string =>
      `Declared complete with ${String(declared)} instance${declared === 1 ? "" : "s"} on ${on}; ${String(settled)} settled. Waiting for the operator's concurrence.`,
    completionSettled: (settled: number, known: number): string =>
      `${String(settled)} of ${String(known)} instance${known === 1 ? "" : "s"} settled. The item is cleared once the contractor declares it complete and the operator concurs.`,
    declareComplete: "Declare complete",
    declareAgain: "Change the declaration",
    declareHeading: (itemNo: string): string => `Declare item ${itemNo} complete`,
    declareLede:
      "Say how many instances this item has in all. The operator concurs once every one is cleared or waived; only then is the item cleared.",
    declareCountLabel: "Number of instances",
    declareCountHint: "A whole number, 1 or more.",
    declareSend: "Declare complete",
    declared: (itemNo: string): string =>
      `Item ${itemNo} declared complete. The operator has a task to concur.`,
    concurComplete: "Concur complete",
    concurHeading: (itemNo: string): string => `Concur that item ${itemNo} is complete`,
    concurLede:
      "Your concurrence clears the item. Every instance must be cleared or waived, and as many settled as declared.",
    concurred: (itemNo: string): string => `Item ${itemNo} concurred complete and cleared.`,
    refusalHoldOrderHeading: "A preceding hold point is not cleared yet",
    refusalItemCompletionHeading: "The item's completion cannot be recorded yet",
    submitForReview: "Submit for review",
    approve: "Approve plan",
    returnToContractor: "Return to contractor",
    revise: "Revise",
    cancelPlan: "Cancel plan",
    reviewCommentLabel: "Review comment",
    recordComment: "Record comment",
    internalNote: "Internal",
    internalToggleLabel: "Internal only (your own side will not see this)",
    reviewMoveComment: "Comment",
    reviewMoveConsult: "Consult",
    reviewMoveAccept: "Accept item",
    reviewMoveObject: "Object to item",
    /* The itemized review loop (M5 P5g, his round-5 #6). Per-item actions, the
       whole-plan gate and the contractor's per-objection reply. */
    itemReviewLabel: "Item review",
    itemCommentLabel: "Comment on this item",
    attachEvidence: "Attach file",
    itemReply: "Reply to objection",
    itemThreadEmpty: "No item-level review moves yet.",
    showItemHistory: (count: number): string =>
      count === 0 ? "Show review history" : `Show review history (${String(count)})`,
    hideItemHistory: "Hide review history",
    approveBlocked: (count: number): string =>
      count === 1
        ? "One item is not yet accepted. Approve is enabled when every item is green."
        : `${String(count)} items are not yet accepted. Approve is enabled when every item is green.`,
    candidatesHeading: "Planning candidates",
    candidatesBody:
      "Accepted hold and witness items owed a date. Each becomes one the moment it is accepted. The planner turns it into a dated activity, or the contractor requests it by RFI.",
    candidateColumnState: "Where it stands",
    candidateWaiting: "Waiting for a date",
    planCandidate: "Plan",
    submitSuccess: "Submitted for review.",
    approveSuccess: (candidates: number): string =>
      `Approved. ${String(candidates)} planning candidates created.`,
    commentRequired: "A comment is required for this move.",
    createHeading: "Create ITP",
    createBody:
      "Register a plan against your main contract. As the main contractor you author every plan for the project, your sub-contractors' scope included. It starts as a draft; you add its items on the plan's own page.",
    createTitleLabel: "Title",
    createContractLabel: "Main contract",
    createDisciplineLabel: "Discipline",
    createSubmit: "Create plan",
    itemAddHeading: "Add an item",
    itemEditHeading: "Edit item",
    itemEdit: "Edit",
    itemAdd: "Add item",
    itemSave: "Save item",
    itemDelete: "Delete item",
    itemCancel: "Cancel",
    itemWorkspaceHeading: "The contractor's workspace",
    itemWorkspaceEditable:
      "The plan is a draft: add, edit and remove items, then submit it for review. After the submit the workspace freezes to read-only and the review exchange takes over.",
    itemWorkspaceFrozen:
      "The plan is under review: the workspace is read-only until the plan is returned, and the review exchange above is where the exchange happens.",
    /* Wave R0 Break 3: the item's interventions, chosen on the item itself. */
    referenceDocument: "Reference document",
    verifyingDocument: "Verifying document",
    remarks: "Remarks",
    sequence: "Order in the plan",
    interventionsLegend: "Intervention points",
    interventionsLoading: "Reading the intervention levels.",
    interventionsUnavailable:
      "The intervention levels could not be read, so the item cannot be saved yet. Reload the page to try again; if it keeps happening, ask your administrator to check the intervention level and party type settings.",
    operatorIntervention: (party: string): string => `${party} intervention`,
    requiredMark: "(required)",
    interventionChoose: "Choose a level",
    interventionNone: "None",
    interventionOption: (label: string, notation: string): string => `${label} (${notation})`,
    operatorHint: (attended: readonly string[]): string =>
      attended.length === 0
        ? "Choose the level of the operator's point on this item."
        : `The plan can be submitted when every item has a level the operator attends: ${attended.join(", ")}.`,
    operatorNotAttended: (attended: readonly string[]): string =>
      attended.length === 0
        ? "The operator does not attend at this level, so the plan cannot be submitted with it."
        : `The operator does not attend at this level, so the plan cannot be submitted with it. Choose one of: ${attended.join(", ")}.`,
    operatorRequired: "Choose the operator's intervention level to save the item.",
    otherInterventionsHeading: "Other parties (optional)",
    interventionColumn: "Intervention",
    noOperatorIntervention: "No operator point",
    interventionTitle: (party: string, level: string): string => `${party}: ${level}`,
    refusalNoOperatorPointHeading: "Some items have no operator point the operator attends",
    refusalItemsNotAcceptedHeading: "Some items are not accepted yet",
  },

  /* ------------------------------------------------------------------ */
  /* Schedules, activities and the date negotiation.                     */
  /* ------------------------------------------------------------------ */
  schedules: {
    heading: "Schedules",
    lede: "The yearly plans and the activities on them. The calendar is evaluated when a date is written and again when a plan is approved.",
    columnPlan: "Plan",
    columnProject: "Project",
    columnStage: "Stage",
    columnYear: "Year",
    columnRevision: "Revision",
    columnState: "State",
    emptyTitle: "No schedules yet",
    emptyBody:
      "A yearly quality plan appears here once it is opened for one of your projects. The plan carries the activities; the activities carry the dates.",
    detailHeading: "Schedule",
    activitiesHeading: "Activities",
    historyHeading: "Phase history",
    calendarHeading: "Calendar periods",
    columnActivity: "Activity",
    columnDate: "Planned date",
    columnType: "Type",
    columnContractor: "Contractor",
    columnStatus: "Status",
    columnPriority: "Priority",
    assessorsHeading: "Assessor team",
    assessorsEmpty:
      "No assessors are visible for this activity. The team either has not been picked yet or is not published to your view.",
    negotiateHeading: "Date negotiation",
    negotiationEmpty: "No moves yet. Acknowledge the date or propose another one.",
    acknowledge: "Acknowledge",
    proposeDate: "Propose date",
    confirmDate: "Confirm date",
    rejectDate: "Reject proposal",
    proposedDateLabel: "Proposed date",
    justificationLabel: "Justification",
    plannedDateLabel: "Planned date",
    transitionSubmit: "Submit plan",
    transitionEndorse: "Endorse plan",
    transitionApprove: "Approve plan",
    transitionReject: "Reject plan",
    transitionDistribute: "Distribute plan",
    transitionRevise: "Revise",
    transitionClose: "Close plan",
    transitionCancel: "Cancel plan",
    activityConduct: "Mark conducted",
    activityClose: "Close activity",
    activityCancel: "Cancel activity",
    createActivityHeading: "Plan an activity",
    candidateLabel: "From planning candidate",
    candidateNone: "None, without a candidate",
    typeLabel: "Verification type identifier",
    typeHelper:
      "The verification type from the plan of record, by its identifier. The register of types arrives with the plan of record configuration work; until then your planner takes it from there.",
    purposeLabel: "Purpose",
    checklistLabel: "Checklist reference",
    priorityLabel: "Priority",
    planActivity: "Plan the activity",
    createSchedule: "Create schedule",
    createScheduleHeading: "Create schedule",
    projectSelectLabel: "Project",
    yearSelectLabel: "Year",
    planTitleLabel: "Plan title",
    createScheduleSuccess: (humanId: string): string => `Schedule ${humanId} created.`,
    advisoryHeading: "Calendar advisories",
    advisoryBody: (period: string): string =>
      `This date sits inside the period "${period}". It did not block the write; it is shown to every approver until final approval.`,
    advisoryOpenPeriod: (period: string): string => `See "${period}" on the calendar.`,
    conflictsHeading: "What this date would collide with",
    conflictsNone: "Nothing on the calendar collides with this date.",
    previewConflicts: "Check the date",
    endorsementLabel: "Endorsement",
    viewYearGrid: "Year grid",
    viewTable: "Table",
    viewMonth: "Month grid",
    monthPrev: "Previous month",
    monthNext: "Next month",
    monthDayHeading: (iso: string): string => `Activities on ${iso}`,
    monthDayEmpty: "No activities planned on this day.",
    yearGridHeading: "The year on one sheet",
    yearGridEmpty: "No activities fall in this year.",
    showShutdown: "Show shutdown periods",
    showRamadan: "Show Ramadan periods",
    rollupHeading: "Quarterly rollup",
    rollupPlanned: "Planned",
    rollupConducted: "Conducted",
    rollupCancelled: "Cancelled",
    rollupFollowUp: "Follow-up",
    rollupRescheduled: "Re-scheduled",
    quarterLabel: (quarter: number): string => `Q${String(quarter)}`,
    signatureHeading: "Signatures",
    preparedBy: "Prepared by",
    endorsedBy: "Endorsed by",
    approvedBy: "Approved by",
    signatureNobody: "Not yet recorded",
    transitionWaiting: (label: string, state: string): string =>
      `${label} waits for the plan to be ${state}.`,
    /* The action-dialog pattern (M5 P5e #8): the confirm lede inside the dialog and
       the plain-language toast the submit leaves at the top of the page. */
    actionConfirm: (label: string): string => `Confirm: ${label}?`,
    actionToastDone: (label: string): string => `${label} recorded.`,
    actionToastRefused: "This action was refused.",
    /* Wave W2: whose turn it is on a planned date, in plain words. */
    negotiation: {
      NOT_DISTRIBUTED: "Not yet sent to the contractor",
      AWAITING_CONTRACTOR: "Waiting for the contractor",
      AWAITING_OPERATOR: "Date proposed, waiting for the operator",
      AGREED: "Date agreed",
      DONE: "Conducted or closed",
    } as Record<string, string>,
    pendingDateNote: (iso: string): string => `Date on the table: ${iso}.`,
    acceptDate: (iso: string): string => `Accept ${iso}`,
    agreeDate: "Agree the proposed date",
    counterPropose: "Propose another date",
    keepPlannedDate: "Keep the planned date",
    keepReasonLabel: "Why the planned date stays",
    moveDone: {
      ACKNOWLEDGE: "Acknowledged. The date is agreed.",
      PROPOSE: "Proposal sent. The other side has a task to answer it.",
      CONFIRM: "Date agreed. It is now the plan's date on both calendars.",
      REJECT: "Answered. The planned date stays; the contractor has a task to answer.",
    } as Record<string, string>,
    openInspectionRecord: "Open the inspection record",
    noMovesYours: "Nothing waits on you for this date.",
    yourActivitiesHeading: "Your planned activities",
    yourActivitiesLede:
      "Acknowledge each date or propose another with a reason. Proposals travel to the operator as a task, and back to you, until both sides agree.",
    yourActivitiesEmpty: "This schedule plans nothing against your organisation.",
    columnDateState: "Date",
    columnAnswer: "Your answer",
    openActivity: "Open",
    legendHeading: "Legend",
    waiting: {
      DRAFT: "This plan is a draft. Its planner submits it for endorsement.",
      SUBMITTED: "Submitted. It waits for the QC lead's endorsement.",
      ENDORSED: "Endorsed. It waits for the head's approval.",
      APPROVED: "Approved. Its planner distributes it to the contractors.",
      DISTRIBUTED: "Distributed. The contractors acknowledge their dates.",
      CLOSED: "Closed.",
      CANCELLED: "Cancelled.",
    } as Record<string, string>,
    rejectReasonLabel: "Reason for the rejection",
    planNeedsCandidateOrContractor:
      "Pick a planning candidate, or a contractor for an activity without one.",
    planNeedsType: "Pick the inspection type.",
    planNeedsDate: "Pick the planned date.",
    typeSelectLabel: "Inspection type",
    contractorLabel: "Contractor",
    endDateLabel: "Last day (optional)",
    checkDateVerdictClear: "Clear: nothing on the calendar collides with this date.",
    checkDateVerdictCaution: (name: string, from: string, to: string): string =>
      `Caution period "${name}" (${from} to ${to}): the date is allowed, and every approver sees this warning.`,
    checkDateVerdictBlocked: (name: string, from: string, to: string): string =>
      `Blocked period "${name}" (${from} to ${to}): this date will be refused. Pick another.`,
    checkDateNeedsDate: "Pick a date to check.",
    planned: (humanId: string): string => `${humanId} planned.`,
  },

  /* ------------------------------------------------------------------ */
  /* The RFI loop (wave W2, Mahmoud's R10).                              */
  /* ------------------------------------------------------------------ */
  rfis: {
    heading: "RFIs",
    lede: "Requests for the operator to attend a hold or witness point, from the request to the agreed date and the inspection. Grouped by contract.",
    contractorLede:
      "Your requests for the operator to attend a hold or witness point. Raise one from an accepted item of an approved plan.",
    emptyTitle: "No RFIs yet",
    emptyBody:
      "An RFI is raised by the main contractor from an accepted hold or witness item of an approved plan, on the plan's own page.",
    columnRfi: "RFI",
    columnItem: "Item",
    columnScope: "Scope",
    columnDate: "Date",
    columnState: "Where it stands",
    columnAction: "Next",
    contractGroup: (contract: string, count: number): string => `${contract} (${String(count)})`,
    dateState: {
      DRAFT: "Draft",
      AWAITING_OPERATOR: "Waiting for the operator",
      AWAITING_CONTRACTOR: "Waiting for the contractor",
      AGREED: "Date agreed",
      CLOSED_WITHOUT_DATE: "Cancelled or rejected",
    } as Record<string, string>,
    yourTurn: "Your turn",
    open: "Open",
    raiseRfi: "Request inspection (RFI)",
    detailHeading: "RFI",
    factsScope: "Scope",
    factsItem: "ITP item",
    factsPlan: "Plan",
    factsContract: "Contract",
    factsContractor: "Contractor",
    factsType: "Point",
    factsLocation: "Location",
    factsDate: "Date",
    factsAssignee: "Assignee",
    factsState: "Where it stands",
    pendingLede: (iso: string): string => `Date on the table: ${iso}.`,
    agreedLede: (iso: string): string =>
      `Agreed for ${iso}. It is in both calendars and routed to inspection.`,
    actionsHeading: "Your answer",
    noMoves: "Nothing waits on you for this request.",
    accept: "Accept the date",
    acceptHeading: "Accept the date and route to inspection",
    acceptLede: (iso: string): string =>
      `The request is accepted for ${iso} and goes to the inspector at once.`,
    assigneeLabel: "Assignee (optional)",
    assigneeChoose: "Select an assignee (optional)",
    inspectorsLabel: "Inspectors to route it to (optional)",
    inspectorsNone: "The inspectors assigned to the contract receive it.",
    counter: "Propose another date",
    counterHeading: "Propose another date",
    counterReasonLabel: "Why, for example the inspector's availability",
    agree: (iso: string): string => `Agree ${iso}`,
    agreeHeading: "Agree the operator's date",
    agreeLede: (iso: string): string => `Both sides then agree ${iso}. It lands in both calendars.`,
    repropose: "Propose another date",
    reproposeReasonLabel: "Note for the operator (optional)",
    dateLabel: "Date",
    send: "Send",
    moveDone: {
      CONFIRM: "Accepted. The date is agreed and the inspector has it.",
      ACKNOWLEDGE: "Agreed. The date is in both calendars.",
      PROPOSE: "Proposal sent. The other side has a task to answer it.",
    } as Record<string, string>,
    delegate: "Delegate conduct",
    delegateHeading: "Delegate conduct",
    delegateLede:
      "Delegate conduct to another conduct-eligible principal. The open conduct task is re-routed and the new assignee is notified.",
    delegateAssigneeLabel: "Assignee",
    delegateChooseAssignee: "Select an assignee",
    delegateReasonLabel: "Note (optional)",
    delegateSend: "Delegate",
    delegatedDone: "Conduct delegated. The new assignee has the task.",
    historyHeading: "History",
    historyTransition: {
      SUBMIT: "Requested",
      APPROVE_DOCS: "Accepted",
      REJECT_DOCS: "Rejected",
      DISTRIBUTE: "Routed to inspection",
      CONDUCT: "Inspection started",
      SUBMIT_RESULTS: "Results submitted",
      APPROVE_RESULTS: "Results approved",
      REJECT_RESULTS: "Results returned",
      CANCEL: "Cancelled",
      ASSIGN: "Delegated",
    } as Record<string, string>,
    historyDateMove: {
      PROPOSE: "Proposed",
      CONFIRM: "Accepted",
      ACKNOWLEDGE: "Agreed",
      REJECT: "Refused",
    } as Record<string, string>,
    openInspection: "Open the inspection record",
    openPlan: "Open the plan",
    fromOperator: (program: string): string =>
      `Requested by the operator from the ${program} program. The contractor agrees the date or proposes another.`,
    factsProgram: "Program",
    factsDiscipline: "Discipline",
    reject: "Reject the request",
    rejectHeading: "Reject the request",
    rejectLede:
      "The request closes, the item can be requested again, and the main contractor is told your reason.",
    rejectReasonLabel: "Why it is rejected",
    rejected: "Rejected. The contractor has your reason.",
    cancel: "Cancel request",
    cancelHeading: "Cancel request",
    cancelLede:
      "Cancel this inspection request. The request closes, the item is released, and the operator is notified.",
    cancelReasonLabel: "Reason (optional)",
    cancelled: "Request cancelled.",
    waive: "Waive request",
    waiveHeading: "Propose a waiver",
    waiveLede:
      "A waiver is completed in two hands: your proposal goes, with its reason, to the engineering authority on the contract, who completes it. Until then the request keeps its state.",
    waiveReasonLabel: "Why the request is waived",
    waiveReasonNeeded: "Say why. The reason is recorded on the waiver and shown to the authority.",
    waivePropose: "Propose the waiver",
    waiverProposed: "Waiver proposed. The engineering authority has a task to complete it.",
    waived: "Request waived.",
    // Wave INST ruling 4: the engineering authority's second hand.
    waiverPendingHeading: "Waiver waiting on the engineering authority",
    waiverPendingMine:
      "You proposed this waiver. Another person holding the engineering authority completes it.",
    waiverPendingOther:
      "An operator proposed this waiver. A person holding the engineering authority on the contract completes it.",
    waiverProposedOn: (at: string): string => `Proposed ${at}.`,
    authorityOpen: "Complete the waiver",
    authorityHeading: "Complete the waiver as the engineering authority",
    authorityLede:
      "The request is then waived, its instance is settled as waived, and the contractor is told the proposer's reason.",
    authorityProposedReason: "The proposer's reason",
    authorityReasonLabel: "Your reason for completing it",
    authorityComplete: "Complete the waiver",
    attachmentsHeading: "Attachments",
    attachmentsEmpty: "No attachments on this request.",
    // Wave INST rulings 1 and 3: the instance and the structured declaration.
    factsInstance: "Instance",
    factsWholeItem: "The whole item (requested before instances were named)",
    factsQuantity: "Quantity",
    factsAsset: "External asset id",
    factsCheckReference: "Internal check reference",
    factsShortNotice: "Short notice",
    shortNotice: "Short notice",
    shortNoticeTitle: "Raised inside the notice lead time, with a justification.",
    // Wave COM item 3: the witness point the operator did not attend.
    witnessHeading: "Witness point",
    witnessUnattended: (at: string): string =>
      `The operator did not attend: marked unattended ${at}.`,
    witnessWaiting: "The operator has not been marked absent. The witness point stands.",
    witnessReleasedOn: (at: string): string => `Released by the contractor ${at}.`,
    witnessReleasedChip: "Witness released",
    witnessDeclarationLabel: "The contractor's declaration",
    releaseWitness: "Release the witness point",
    releaseHeading: "Release the witness point",
    releaseLede:
      "The operator missed the agreed witness point. Released, the inspection proceeds on your own declaration and evidence, and the operator reviews it after the fact.",
    releaseDeclarationLabel: "What you inspected and found",
    releaseDeclarationNeeded: "Say what you inspected and found. The release is recorded with it.",
    releaseEvidenceLabel: "Evidence",
    releaseEvidenceEmpty: "No evidence file chosen.",
    releaseSend: "Release the witness point",
    released: "Witness point released. The operator is told and reviews it after the fact.",
    refusalWitnessHeading: "The witness point cannot be released",
    // Wave RFI rulings 1, 4 and 5 (b12 frontend item 7).
    itemsHeading: "Items of this request",
    itemsColumnItem: "Item",
    itemsColumnInstance: "Instance",
    itemsColumnQuantity: "Quantity",
    itemsCount: (count: number): string => `${String(count)} item${count === 1 ? "" : "s"}`,
    filesCount: (count: number): string => `${String(count)} file${count === 1 ? "" : "s"}`,
    fileUnnamed: (index: number): string => `File ${String(index)}`,
    assign: "Assign",
    reassign: "Change the assignee",
    assignHeading: "Name the assignee",
    assignLede:
      "One person conducts this inspection: once named, only they can start it. The assignee can be changed until conduct starts.",
    assignLabel: "Assignee",
    assignChoose: "Select the person",
    assignReasonLabel: "Reason (optional)",
    assignSend: "Assign",
    assigned: (name: string): string => `${name} is the assignee.`,
    refusalAssignHeading: "The assignee was not changed",
    refusalNotAssigneeHeading: "Only the assignee starts this inspection",
  },

  /* ------------------------------------------------------------------ */
  /* The inspection day: conducting against the category-born checklist, */
  /* the saved draft, the results approval cycle (Mahmoud, 2026-10-11).  */
  /* ------------------------------------------------------------------ */
  conduct: {
    begin: "Conduct the inspection",
    continueDraft: "Continue the inspection",
    title: (humanId: string): string => `Conduct ${humanId}`,
    introHeading: "Before you start",
    introBody:
      "The program's checklist loads for this inspection. Answer each item Pass, Fail or Not applicable, add remarks, observations and evidence. Save whenever you stop; the draft waits on the server for you or a colleague on the team.",
    beginNow: "Begin the inspection",
    beginBusy: "Loading the checklist...",
    notOpen:
      "This inspection is not open for you to conduct now. It opens on its agreed date for the inspectors on its contract.",
    program: "Program",
    discipline: "Discipline",
    contract: "Contract",
    contractor: "Contractor",
    date: "Date",
    location: "Location",
    checklist: "Checklist",
    activity: "Planned activity",
    noChecklistName: "The requested point only",
    savedBy: (who: string, at: string): string => `Last saved by ${who}, ${at}.`,
    notSavedYet: "Not saved yet.",
    returnedHeading: "The results came back to you",
    progress: (answered: number, total: number): string =>
      `${String(answered)} of ${String(total)} items answered`,
    failedCount: (count: number): string =>
      count === 1 ? "1 item failed" : `${String(count)} items failed`,
    requestedPoint: "The requested point",
    sectionHeading: (number: string | null, title: string | null): string =>
      [number, title].filter((one) => one !== null && one !== "").join(" "),
    itemLabel: (number: string): string => `Item ${number}`,
    acceptance: "Acceptance",
    markGroup: (number: string): string => `Answer for item ${number}`,
    marks: {
      PASS: "Pass",
      FAIL: "Fail",
      NOT_APPLICABLE: "Not applicable",
    } as Record<string, string>,
    remarksLabel: "Remarks",
    remarksFailLabel: "What failed (required)",
    failNeedsReason:
      "Say what failed, in the remarks or as a negative observation. The contractor rectifies from this sentence.",
    observationsHeading: "Observations",
    observationAdd: "Add an observation",
    observationKindLabel: "Kind",
    observationKinds: {
      POSITIVE: "Positive",
      NEGATIVE: "Negative",
    } as Record<string, string>,
    observationTextLabel: "Observation",
    observationActionLabel: "The contractor must act on it",
    observationActionHelper:
      "Shared for information either way. Ticked, it reaches the contractor as a finding to answer.",
    observationSave: "Add",
    observationCancel: "Cancel",
    observationRemove: (text: string): string => `Remove the observation: ${text}`,
    observationForAction: "for action",
    observationForInformation: "for information",
    evidenceToggle: "Evidence",
    evidenceAttach: "Attach evidence",
    evidenceUploading: "Uploading...",
    evidenceEmpty: "No evidence on this item yet.",
    save: "Save draft",
    saving: "Saving...",
    saved: "Saved. You can close and continue later, on any device.",
    submit: "Submit for approval",
    submitHeading: "Submit the checklist for results approval",
    submitLede:
      "The next step up the approval chain approves the results or returns them with a reason. Nothing reaches the contractor before the approval.",
    submitFindings: (count: number): string =>
      count === 0
        ? "No item failed: no finding will be born."
        : count === 1
          ? "1 failed item becomes a finding sent to the contractor at approval."
          : `${String(count)} failed items become findings sent to the contractor at approval.`,
    submitObservations: (count: number): string =>
      count === 0
        ? "No observation recorded."
        : `${String(count)} observation(s) are shared with the contractor at approval.`,
    submitConfirm: "Submit",
    submitBusy: "Submitting...",
    submitted: "Submitted. The next step up the approval chain has the results to approve.",
    submitBlockedUnanswered: (count: number): string =>
      `${String(count)} item(s) still have no answer. Answer every item to submit; saving works at any point.`,
    submitBlockedUnexplained: (count: number): string =>
      `${String(count)} failed item(s) do not say what failed.`,
    submitBlockedNoRight:
      "You may save answers here; submitting results belongs to whoever conducts on this contract: the discipline inspector, the QA/QC engineer or the QC lead.",
    restored:
      "Answers you had not saved on this device are back in the form. Save them, or discard them to read the saved checklist.",
    restoredStale:
      "This device kept answers you had not saved, but someone saved the checklist since. They are not in the form.",
    restoreAnyway: "Show my unsaved answers",
    discardLocal: "Discard my unsaved answers",
    reload: "Reload the saved checklist",
    close: "Close",
    approve: "Approve the results",
    approveHeading: "Approve the inspection results",
    approveLede:
      "Approving locks the answers. Every failed item becomes a finding sent to the main contractor to rectify; the observations are shared with it.",
    approved: "Approved. Findings and observations went to the contractor.",
    returnResults: "Return to the inspector",
    returnHeading: "Return the results to the inspector",
    returnReasonLabel: "Why they go back",
    returned: "Returned. The inspector has a task to correct and submit again.",
    approvalWaits: "Results submitted. They wait for approval one step up the approval chain.",
    /* The approval chain (Mahmoud's ruling 2026-10-11): where the results go, in role
       words, stated before the submission and while they wait. */
    approvalWaitsFor: (roles: string): string =>
      `Results submitted. They wait for the approval of the ${roles}, the next step up the approval chain.`,
    routeBefore: (roles: string): string =>
      `When you submit, the results go one step up the approval chain, to the ${roles}, for approval.`,
    routeSkipped: (roles: string): string =>
      `Nobody in the ${roles} role may approve this inspection, so the results go further up.`,
    routeNobody:
      "Nobody above you in the approval chain may approve this inspection yet, so the results would wait with nobody to approve them. Ask for a QA/QC engineer on the contract first.",
    resultsApprovedBy: (role: string, date: string): string =>
      `results approved by ${role} on ${date}`,
    cannotApproveAlreadyApproved: (role: string, date: string): string =>
      `Cannot approve: results are already approved (results approved by ${role} on ${date}).`,
    cannotApproveOutranked: (routeSentence: string): string =>
      `Cannot approve: the approver must outrank the conductor. ${routeSentence}`,
    awaitingClose: "Awaiting close",
    awaitingCloseBlocked: (reasons: string): string =>
      `Awaiting close by QC Lead. Blocked by: ${reasons}.`,
    awaitingCloseReady:
      "All child findings and punch items are settled. Verification is ready to close by the QC Lead.",
    awaitingAcknowledgement: "Results approved. Awaiting contractor acknowledgement.",
    closeVerification: "Close verification",
    closedSuccess: "Verification closed.",
  },

  /* ------------------------------------------------------------------ */
  /* Inspection conduct: the checklist as the report card.               */
  /* ------------------------------------------------------------------ */
  inspections: {
    heading: "Inspections",
    lede: "The inspection records you may see, from request to closure. Open one to conduct it or to read what was recorded.",
    columnRecord: "Record",
    columnTitle: "Title",
    columnDate: "Planned date",
    columnType: "Type",
    columnContractor: "Contractor",
    columnState: "State",
    columnOutcome: "Outcome",
    statusWaitingApproval: "Waiting for approval",
    statusResultsApproved: "Results approved",
    statusAwaitingClose: "Awaiting close",
    emptyTitle: "No inspection records yet",
    emptyBody:
      "An inspection record appears here when it is requested against an approved plan item. Conduct it from here when the date arrives.",
    detailHeading: "Inspection record",
    linesHeading: "Checklist lines",
    lineNo: "Line",
    description: "Item",
    acceptance: "Acceptance criteria",
    outcome: "Outcome",
    lockedBadge: "Locked",
    positiveBadge: "Positive observation",
    lineOutcomePlaceholder: "Not yet recorded",
    attachmentsHeading: "Evidence on this line",
    attachLabel: "Attach evidence",
    attachButton: "Attach",
    uploading: "Uploading...",
    attachEmpty: "No evidence attached to this line yet.",
    attachmentFailure: "The upload was refused. Try again.",
    beginConduct: "Begin conduct",
    checklistMarkHeading: "Checklist marks",
    checklistMarkLede:
      "The numbered sections are the bank's instrument; mark each line YES, NO or N/A. The marks record as the line outcomes they map to.",
    markYes: "YES",
    markNo: "NO",
    markNotApplicable: "N/A",
    submitResults: "Submit results",
    approveResults: "Approve results",
    rejectResults: "Reject results",
    acknowledge: "Acknowledge",
    closeRecord: "Close record",
    requestDocs: "Request documents",
    approveDocs: "Approve documents",
    rejectDocs: "Reject documents",
    distribute: "Distribute to contractor",
    declarationLabel:
      "I confirm the outcomes recorded above were observed on site and are recorded as they were observed.",
    declarationConfirm: "I confirm",
    submitDeclarationRequired: "Confirm the declaration before submitting.",
    resultsBannerHeading: "Result",
    evaluatedUnder: (rule: string): string =>
      rule === "decision_rule/core-default-all-lines-pass"
        ? "Every checklist line was judged under the plan's default rule: each line must be accepted."
        : "This record was judged under the project's own decision rule.",
    ruleUpdatedSince:
      "The decision rule changed after this record was judged. Re-evaluate to judge it under the current rule.",
    reEvaluate: "Re-evaluate",
    outcomeHeader: "Record outcome",
    /* Wave R0 Break 9: a close that carried a rejected line. */
    closedWithRejections: "Closed with rejections",
    closedWithRejectionsHint:
      "Closed with rejected lines once every finding was settled. The inspected item is open for a new inspection request after the rework.",
    distributionHeading: "Outcome distribution",
    distributionEmpty: "No outcomes recorded yet.",
    groupToggle: "Group by outcome",
    flatToggle: "Show as recorded",
    unrecordedGroup: "Not yet recorded",
    bornFinding: "Born finding",
    actionFromFinding: "Action request",
    conductAtLabel: "Conducted",
    locationLabel: "Location",
    componentLabel: "Component",
    procedureLabel: "Procedure or drawing",
    originLabel: "Requested by",
    originRfi: "The contractor's RFI (open the request)",
    originOperatorRequest: "The operator's inspection request (open the request)",
    originActivity: "A planned activity of the schedule (open the activity)",
  },

  /* ------------------------------------------------------------------ */
  /* Findings, the punch list and the response loop.                     */
  /* ------------------------------------------------------------------ */
  findings: {
    heading: "Findings",
    originHeading: "Where this finding came from",
    originItem: (number: string, text: string, checklist: string | null): string =>
      checklist === null
        ? `Item ${number}: ${text}`
        : `Checklist ${checklist}, item ${number}: ${text}`,
    originInspection: (humanId: string, title: string): string => `Inspection ${humanId}, ${title}`,
    originRfi: "requested by the contractor's RFI",
    originOperatorRequest: "requested by the operator",
    originActivity: (humanId: string): string => `planned activity ${humanId}`,
    lede: "Everything observed on inspection lines that needed a record: nonconformances, comments, positive observations and improvements.",
    columnFinding: "Finding",
    columnClass: "Class",
    columnState: "State",
    columnObserved: "Observed",
    columnDaysOpen: "Days open",
    observedByLabel: "Observed by",
    observedOnLabel: "Observed on",
    descriptionLabel: "Description",
    subjectLabel: "Subject item",
    discoveredAfterRelease: "Discovered after release",
    verificationLabel: "Born on inspection record",
    closureLabel: "Closure",
    punchHeading: "Punch list",
    punchListLabel: "Punch list",
    punchListPlaceholder: "Choose a punch list",
    punchListClosedRefusal: "This punch list is closed and cannot accept new findings.",
    addToPunchList: "Add to punch list",
    completionNotesLabel: "Completion notes",
    markCompleted: "Mark completed",
    acceptPunch: "Accept punch",
    acceptPunchBlocked: "You recorded this completion; another inspector must accept it.",
    /* Wave W2 (Mahmoud's ruling 2026-10-10): insertion and completion are the operator's. */
    punchOperatorNote:
      "The operator puts findings on a punch list and marks punch items complete. You are notified each time.",
    rejectPunch: "Reject completion",
    punchAcceptClosure: "Closure reference",
    punchAcceptComments: "Closure comments",
    punchRejectReason: "Rejection reason",
    responseHeading: "Response loop",
    responseEmpty: "No responses yet.",
    roundLabel: (round: number): string => `Round ${String(round)}`,
    proposedCorrection: "Proposed correction",
    proposedCorrectiveAction: "Proposed corrective action",
    proposedTargetDate: "Proposed target date",
    proposeResponse: "Propose response",
    requestResponse: "Request response",
    agreeResponse: "Agree",
    rejectResponse: "Return for revision",
    decisionCommentLabel: "Decision comment",
    declareImplemented: "Declare implemented",
    implementedEvidence: "Implementation evidence",
    verifyClose: "Verify and close",
    verifyClosureRef: "Closure reference",
    verifyClosureComments: "Closure comments",
    promotionHeading: "Escalation suggestion",
    promotionNone: "No escalation suggested",
    promotionSuggestLabel: "Suggest promotion",
    promotionPlaceholder: "Not suggested",
    promotionNcr: "Non-conformance report (NCR)",
    promotionCar: "Corrective action request (CAR)",
    promoteButton: (type: string): string => `Promote to ${type}`,
    promoteTitleLabel: "Action title",
    promoteDescriptionLabel: "Action description",
    distributeForResponse: "Distribute for response",
    distributeHeading: "Distribute for response",
    distributeLede:
      "Distribute this finding to the contractor for formal response and corrective action.",
    distributeCommentLabel: "Note to contractor (optional)",
    distributeSubmit: "Distribute",
    distributeDone: "Finding distributed to contractor for response.",
    /* Wave R0 Break 7: the implementation leg. */
    implementationHeading: "Implementation",
    declareImplementation: "Declare implementation",
    declareImplementationLede:
      "Say what was done to carry out the agreed correction and attach the evidence. The operator then verifies it on site and closes the finding or sends it back.",
    implementationNote: "What was done",
    implementationNoteRequired: "Say what was done before declaring it.",
    implementationFiles: "Evidence files",
    implementationFilesEmpty: "No evidence attached yet.",
    implementationDeclared: "Implementation declared. The operator has been asked to verify it.",
    implementationDeclaredOn: (date: string): string => `Declared implemented on ${date}.`,
    implementationEvidenceLoading: "Reading the evidence files.",
    implementationEvidenceFailed:
      "The evidence files could not be read. Reload the page to try again.",
    implementationEvidenceNone: "No evidence file was attached to the declaration.",
    implementationAwaitingContractor:
      "The correction is agreed. The contractor declares it implemented when the work is done.",
    implementationAwaitingOperator:
      "The implementation is declared. The operator verifies it and closes the finding or sends it back.",
    implementationOwn:
      "You declared this implementation, so another person verifies it. Nobody verifies their own work.",
    verifyImplementation: "Verify closure",
    verifyImplementationLede:
      "Close the finding when the correction is verified, or send it back to the contractor with the reason.",
    verifyDecisionLabel: "Decision",
    verifyDecisionClose: "Close the finding",
    verifyDecisionReject: "Send it back",
    verifyReasonRequired: "Reason (required to send it back)",
    verifyReasonOptional: "Closure comments",
    verifyReasonMissing:
      "Say why the implementation is not accepted, so the contractor knows what to redo.",
    implementationClosed: "Finding closed.",
    implementationRejected: "Sent back to the contractor with your reason.",
    refusalNotAgreedHeading: "This finding is not waiting for an implementation",
    refusalNotAgreedBody:
      "An implementation is declared only once the operator has agreed the response. Reload the finding to see where it stands.",
    refusalNotImplementedHeading: "This finding has no implementation to verify",
    refusalNotImplementedBody:
      "Closure is verified once the contractor has declared the implementation. Reload the finding to see where it stands.",
    refusalSelfApprovalHeading: "Another person must approve this",
    stateEmptyTitle: "No findings yet",
    stateEmptyBody:
      "Findings are born on inspection lines when the recorded outcome needs one: a rejection or a comment. They will appear here the moment they exist.",
    filterClass: "Class",
    filterState: "State",
    all: "All",
  },

  /* ------------------------------------------------------------------ */
  /* The action engine.                                                  */
  /* ------------------------------------------------------------------ */
  actions: {
    selfApprovalBlocked:
      "You submitted this record, so another approver must approve and issue it.",
    selfDispositionBlocked:
      "You proposed this disposition, so another person must decide it and verify its closure.",
    heading: "Actions",
    lede: "The formal records: nonconformance reports, corrective and preventive actions. One engine, every type.",
    columnAction: "Action",
    columnType: "Type",
    columnTitle: "Title",
    columnContractor: "Contractor",
    columnState: "State",
    columnRound: "Round",
    detailHeading: "Action record",
    originLabel: "Promoted from finding",
    noOrigin: "Raised directly",
    descriptionLabel: "Description",
    dispositionHeading: "Disposition",
    dispositionTypeLabel: "Disposition type",
    dispositionTypePlaceholder: "Choose a disposition",
    justificationLabel: "Justification",
    justificationDisabledReason: "Write a justification first",
    concessionLabel: "Concession reference",
    concessionHelper:
      "Required for a use-as-is disposition. The system refuses the proposal without it.",
    proposeDisposition: "Propose disposition",
    currentDisposition: "Proposed disposition",
    issueLabel: "Issue to contractor",
    approveDispositionHeading: "Review the disposition",
    rootCauseCategoryLabel: "Root cause category",
    rootCauseCategoryPlaceholder: "Choose a category",
    rootCauseAnalysisLabel: "Root cause analysis",
    correctivePlanLabel: "Corrective action plan",
    targetDateLabel: "Target implementation date",
    approveDisposition: "Approve disposition",
    rejectDisposition: "Return disposition",
    rejectCommentLabel: "Comment",
    implementationHeading: "Implementation",
    evidenceTextLabel: "Evidence text",
    fileRefLabel: "File or document reference",
    addEvidence: "Add evidence",
    declareImplementation: "Declare implemented",
    verifyClosureHeading: "Verification of closure",
    closureNotesLabel: "Closure notes",
    verifyClosure: "Verify closure",
    /* Wave R0 Break 11: the verifier sets the effectiveness review. */
    effectivenessReviewRequiredLabel: "Effectiveness review required (+90 days)",
    effectivenessReviewRequiredHint:
      "Ticked, the record waits 90 days for an effectiveness review before it closes. Unticked, it closes now.",
    effectivenessReviewDue: (date: string): string => `Effectiveness review due ${date}`,
    effectivenessHeading: "Effectiveness review",
    effectivenessScheduled: (date: string): string => `Review scheduled for ${date}`,
    effectivenessOutcomeLabel: "Outcome",
    effectivenessEffective: "Effective",
    effectivenessNotEffective: "Not effective",
    effectivenessNotesLabel: "Effectiveness notes",
    recordEffectiveness: "Save effectiveness",
    followUpNote: "A not-effective answer offers a follow-up action linked back to this one.",
    historyHeading: "Phase history",
    stateEmptyTitle: "No actions yet",
    stateEmptyBody:
      "Formal actions begin as findings promoted by the quality engineer, or as direct submissions. They will appear here the moment one exists.",
    filterType: "Type",
    filterState: "State",
    // Wave INST rulings 4 and 8: the engineering authority on a disposition.
    proposeToAuthority: "Propose to the engineering authority",
    authorityNote:
      "This disposition needs the engineering authority. Your approval proposes it; a second person holding the engineering authority on the contract completes it, or it is returned to the contractor.",
    authorityPendingMine:
      "You proposed this disposition to the engineering authority. Another person holding the authority completes it, or it is returned to the contractor.",
    authorityPendingOther:
      "Waiting for the engineering authority on the contract to complete this disposition, or for it to be returned to the contractor.",
    authorityProposedOn: (at: string): string => `Proposed to the authority ${at}.`,
    authorityProposedBy: "Proposed by",
    authorityComplete: "Complete as the engineering authority",
    authorityHeading: "Complete the disposition as the engineering authority",
    authorityLede:
      "Completing it agrees the disposition: the contractor implements it. Return it instead if it should not stand.",
    authorityReturn: "Return to the contractor",
    authorityReturnLede:
      "The disposition goes back to the contractor for revision, with your comment. The authority's task closes.",
    authorityCompleted:
      "Disposition completed by the engineering authority. The contractor implements it.",
    authorityReturned: "Disposition returned to the contractor with your comment.",
  },

  /* ------------------------------------------------------------------ */
  /* Wave AUD frontend: record history drawer, audit register and heads. */
  /* ------------------------------------------------------------------ */
  audit: {
    historyButton: "History",
    historyHeading: (label?: string): string =>
      label !== undefined && label !== "" ? `History: ${label}` : "History",
    historyEmpty: "No audit entry is recorded for this record yet.",
    historyPart: (table: string, objectType: string): string =>
      table === objectType ? table : `${table} (${objectType})`,
    changeLine: (field: string, before: string, after: string): string =>
      `${field}: ${before} -> ${after}`,
    noChanges: "No column changes",
    unknownActor: "System",
    close: "Close",
    heading: "Audit register",
    lede: "Every hash-chained entry across the tenant's fourteen families, newest first, with each chain's verification verdict and heads.",
    chainsHeading: "Chain verification",
    chainIntact: "intact",
    chainBroken: (seq: number | null, reason: string | null): string =>
      `broken at seq ${String(seq ?? 0)}: ${reason ?? "unknown"}`,
    headsHeading: "Chain heads",
    headsLede:
      "Each family's current head and genesis instant. Download the JSON to verify against an off-host copy.",
    downloadHeads: "Download heads",
    headsCaption: "The chain heads",
    headFamilyColumn: "Family",
    headSeqColumn: "Head sequence",
    headHashColumn: "Head hash",
    headGenesisColumn: "Genesis instant",
    headCountColumn: "Count",
    noHeads: "No chain head is recorded yet.",
    filtersHeading: "Filters",
    familyLabel: "Family",
    familyAll: "Every family",
    objectTypeLabel: "Object type",
    objectTypePlaceholder: "For example PROJECT",
    objectIdLabel: "Object id",
    objectIdPlaceholder: "UUID",
    actorLabel: "Actor",
    actorPlaceholder: "Person UUID",
    eventLabel: "Event",
    eventAll: "Every event",
    sinceLabel: "Since",
    untilLabel: "Until",
    limitLabel: "Page size",
    applyFilters: "Apply filters",
    clearFilters: "Clear filters",
    entriesCaption: "The audit register",
    emptyTitle: "No audit entry matches",
    emptyBody: "Adjust or clear the filters to see more entries.",
    columnWhen: "When",
    columnFamily: "Family",
    columnSeq: "Sequence",
    columnEvent: "Event",
    columnObject: "Object",
    columnActor: "Actor",
    columnChanges: "Changes",
    pageSummary: (offset: number, shown: number, total: number): string =>
      total === 0
        ? "0 of 0"
        : `${String(offset + 1)} to ${String(offset + shown)} of ${String(total)}`,
    previousPage: "Previous page",
    nextPage: "Next page",
    refusalRangeEmptyHeading: "The date range is empty",
    configChainIntact: (checked: number): string =>
      `CONFIGURATION chain intact (${String(checked)} entries checked)`,
    configChainBroken: (seq: number | null, reason: string | null): string =>
      `CONFIGURATION chain broken at seq ${String(seq ?? 0)}: ${reason ?? "unknown"}`,
  },

  /* ------------------------------------------------------------------ */
  /* The configuration surface. Internal-only navigation; the API        */
  /* decides who that is, and this screen renders the answer.            */
  /* ------------------------------------------------------------------ */
  configuration: {
    heading: "Configuration",
    lede: "Your tenant's own terminology, decision rules and calendar enforcement. Every change lands in the audit trail and takes effect on the next record, with no deploy.",
    terminologyHeading: "Terminology",
    terminologyBody:
      "Rename the words this deployment uses. The vocabulary tokens stay canonical; only the rendered labels change.",
    rulesHeading: "Decision rules",
    rulesBody:
      "The rule a verification is judged under, versioned. Publishing a version never rewrites an open record; the record shows what it was judged under.",
    calendarHeading: "Calendar enforcement",
    calendarBody:
      "Override how a single period enforces itself: block the write or warn and allow.",
    auditHeading: "Audit trail",
    auditBody: "Every configuration change, hash-chained and tamper evident.",
    terminologyEmpty:
      "No terminology overrides yet. Every label in the interface renders the default vocabulary.",
    futureWork:
      "The editor for this panel is future work, not yet built. Nothing here is editable in this deployment, and no placeholder data is shown.",
    saveTerminology: "Save term",
    termLabel: "Term",
    valueLabel: "Renders as",
    localeLabel: "Locale",
    publishVersion: "Publish version",
    ruleFamilyLabel: "Rule family",
    ruleDefinitionLabel: "Definition",
    enforcementBlock: "Block",
    enforcementWarn: "Warn only",
    auditSeq: "Sequence",
    auditEvent: "Event",
    auditObject: "Object",
    auditActor: "Actor",
    auditWhen: "When",
    // Wave INST ruling 3: the RFI notice lead time, per verification type.
    verificationTypesHeading: "Verification types",
    verificationTypesBody:
      "How many calendar days of notice the contractor gives before an RFI's proposed date, per type. An RFI proposed closer than this is refused unless it says why the inspection cannot wait; it is then flagged as short notice for the operator.",
    verificationTypesEmpty: "No verification type is configured for this tenant yet.",
    verificationTypeColumn: "Verification type",
    noticeLeadColumn: "RFI notice lead time",
    notificationLeadColumn: "Operator's notice of a planned assessment",
    noLead: "None",
    days: (days: number): string => `${String(days)} day${days === 1 ? "" : "s"}`,
    noticeLeadEdit: "Set the notice",
    noticeLeadHeading: (type: string): string => `RFI notice lead time: ${type}`,
    noticeLeadLede:
      "The contractor's notice before the proposed date of an RFI of this type, in calendar days. It does not change the operator's notice of a planned assessment, which runs the other way.",
    noticeLeadLabel: "Notice lead time, in calendar days",
    noticeLeadHint: "0 to 365. Leave empty for no lead time.",
    noticeLeadWhole: "A whole number of days, or empty for none.",
    noticeLeadSave: "Save the notice",
    noticeLeadClear: "Clear the notice",
    noticeLeadSaved: (type: string, days: number | null): string =>
      days === null
        ? `${type}: no notice lead time.`
        : `${type}: RFIs need ${String(days)} day${days === 1 ? "" : "s"} of notice.`,
    // Wave b12 frontend item 5 (wave COM item 1): the task clocks and the witness grace.
    taskClocksHeading: "Task clocks",
    taskClocksBody:
      "How long each kind of task has before it falls due, when its reminder rings, and when an overdue task is escalated. A task opened after a change takes the new clock; a task already open keeps the due time it was born with.",
    taskClocksEmpty: "No task clock is stored for this tenant.",
    clockKindColumn: "Task",
    clockDueColumn: "Due after",
    clockReminderColumn: "Reminder",
    clockEscalationColumn: "Escalated",
    clockEscalateToColumn: "Escalated to",
    noDueTime: "No due time",
    hours: (hours: number): string => `${String(hours)} hour${hours === 1 ? "" : "s"}`,
    reminderAt: (percent: number): string => `At ${String(percent)}% of the clock`,
    escalationAfter: (hours: number): string =>
      hours === 0
        ? "At the due time"
        : `${String(hours)} hour${hours === 1 ? "" : "s"} after the due time`,
    escalateToLead: "The QC lead",
    escalateToRank: (rank: number): string => `Approval chain rank ${String(rank)}`,
    clockEdit: "Change the clock",
    clockHeading: (kind: string): string => `Task clock: ${kind}`,
    clockLede:
      "The task's due time is counted from when it opens, in the tenant's zone. The scheduler rings the reminder, marks the task overdue at its due time, and escalates it after the hours set here.",
    clockDueLabel: "Hours until due",
    clockDueHint:
      "1 to 8760. Leave empty for a kind with no due time (no reminder, no escalation).",
    clockReminderLabel: "Reminder, as a percentage of the clock",
    clockReminderHint: "1 to 99: the reminder rings once this share of the clock has passed.",
    clockEscalationLabel: "Hours after the due time before escalation",
    clockEscalationHint: "0 to 8760. 0 escalates at the due time.",
    clockRankLabel: "Escalate to approval chain rank",
    clockRankHint: "1 to 20. Leave empty to escalate to the QC lead.",
    clockWhole: "Whole numbers only, inside the ranges given.",
    clockSave: "Save the clock",
    clockSaved: (kind: string): string => `${kind}: clock saved.`,
    graceHeading: "Witness grace",
    graceBody: (hours: number): string =>
      `An agreed witness point the operator has not started ${String(hours)} hour${hours === 1 ? "" : "s"} after the start of its agreed day is marked unattended; the contractor may then release it.`,
    graceEdit: "Change the grace",
    graceDialogHeading: "Witness grace hours",
    graceLabel: "Hours after the start of the agreed day",
    graceHint: "0 to 720, counted in the tenant's zone.",
    graceSave: "Save the grace",
    graceSaved: (hours: number): string =>
      `Witness grace saved: ${String(hours)} hour${hours === 1 ? "" : "s"}.`,
    // Wave b12 frontend item 6 (wave COM item 5): the webhook endpoints.
    webhooksHeading: "Webhooks",
    webhooksBody:
      "Receiving systems on the public internet that Qualify calls when a task opens or closes, a bell rings or a record moves. Each call is signed with the secret held in the environment variable the endpoint names; the secret itself is never entered or shown here.",
    webhooksEmpty: "No webhook endpoint is registered for this tenant.",
    webhookAdd: "Add an endpoint",
    webhookUrlColumn: "Endpoint",
    webhookEventsColumn: "Events",
    webhookSecretColumn: "Signing secret",
    webhookStateColumn: "State",
    webhookActive: "Active",
    webhookInactive: "Inactive",
    webhookFingerprint: (ref: string, fingerprint: string): string =>
      `${ref} (fingerprint ${fingerprint})`,
    webhookEdit: "Change",
    webhookDeactivate: "Deactivate",
    webhookActivate: "Activate",
    webhookCreateHeading: "Add a webhook endpoint",
    webhookEditHeading: (url: string): string => `Webhook endpoint: ${url}`,
    webhookUrlLabel: "URL",
    webhookUrlHint: "An https address of a receiving system on the public internet.",
    webhookSecretLabel: "Signing secret variable",
    webhookSecretHint:
      "The NAME of the environment variable that holds the signing secret, for example QUALIFY_WEBHOOK_SECRET_PRIMARY. Never the secret itself.",
    webhookEventsLabel: "Events this endpoint receives",
    webhookEventsNeeded: "Choose at least one event.",
    webhookActiveLabel: "Active: calls are sent",
    webhookSave: "Save the endpoint",
    webhookCreated: "Endpoint added.",
    webhookSaved: "Endpoint saved.",
    webhookActivated: "Endpoint activated.",
    webhookDeactivated: "Endpoint deactivated: no call is sent to it.",
  },

  /* ------------------------------------------------------------------ */
  /* Procurement surveillance: the criticality of an equipment line      */
  /* (wave INST ruling 11: every rating names its line).                 */
  /* ------------------------------------------------------------------ */
  criticality: {
    heading: "Equipment criticality",
    lede: "The current criticality rating of each equipment line of this contract. One current rating per line: a line is rated once and revised after.",
    emptyTitle: "No equipment line rated yet",
    emptyBody:
      "Rate a line by its requisition item or tag. Its level sets the surveillance it receives.",
    columnRating: "Rating",
    columnLine: "Equipment line",
    columnType: "Equipment type",
    columnLevel: "Level (total)",
    columnRated: "Rated",
    revision: (revision: number): string => `rev ${String(revision)}`,
    noLine: "No line named (rated before lines were named)",
    rateOpen: "Rate an equipment line",
    noProject: "This contract is under no project yet, so no line of it can be rated.",
    rateHeading: "Rate an equipment line",
    rateLede:
      "Name the line, choose its equipment type and score each element. The total and the level are computed from the tenant's scheme when you save.",
    lineLabel: "Equipment line",
    lineHint: "The requisition item or tag, for example a pump's tag number.",
    typeLabel: "Equipment type",
    typeChoose: "Choose an equipment type",
    scoresLabel: "Scores",
    scoreLabel: (element: string, scaleMax: number): string =>
      `${element} (1 to ${String(scaleMax)})`,
    noScheme: "This tenant has no criticality elements configured yet, so no line can be rated.",
    noteLabel: "Note (optional)",
    missing: (fields: readonly string[]): string =>
      `Still needed before saving: ${fields.join(", ")}.`,
    rateSend: "Save the rating",
    rated: (humanId: string, line: string, level: string): string =>
      `${humanId} rates line ${line}: ${level}.`,
    refusalLineRatedHeading: "This line is already rated",
  },

  /* ------------------------------------------------------------------ */
  /* The guide.                                                          */
  /* ------------------------------------------------------------------ */
  guide: {
    heading: "The walkthrough",
    lede: "From sign-in to a closed finding, one action at a time. Each step names the screen you are on and what success looks like.",
    stepOf: (n: number, total: number): string => `Step ${String(n)} of ${String(total)}`,
    screenLabel: "Screen",
    successLabel: "Success looks like",
    backToDashboard: "Back to the dashboard",
    intro:
      "This is the walkthrough the test guide in the handbook follows. Do it once with the sandbox account to explore, then again with your own accounts for the real walk.",
  },

  /* ------------------------------------------------------------------ */
  /* Status vocabulary. The enum tokens are canonical and the rendered   */
  /* labels live here; the tenant override layer replaces them at        */
  /* runtime when the configuration service lands.                       */
  /* ------------------------------------------------------------------ */
  terms: {
    workflowStates: {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      IN_REVIEW: "In review",
      APPROVED: "Approved",
      RETURNED: "Returned",
      REVISION: "Revision",
      ENDORSED: "Endorsed",
      REJECTED: "Rejected",
      DISTRIBUTED: "Distributed",
      CLOSED: "Closed",
      CANCELLED: "Cancelled",
      REQUESTED: "Requested",
      DOCUMENTS_REQUESTED: "Documents requested",
      DOCUMENTS_APPROVED: "Documents approved",
      ASSIGNED: "Assigned",
      READY: "Ready",
      IN_ACTION: "In action",
      RESULTS_SUBMITTED: "Results submitted",
      FINAL_APPROVAL: "Final approval",
      ACKNOWLEDGED: "Acknowledged",
      OPEN: "Open",
      RECORDED: "Recorded",
      IN_PUNCH_LIST: "In punch list",
      AWAITING_RESPONSE: "Awaiting response",
      RESPONSE_PROPOSED: "Response proposed",
      AGREED: "Agreed",
      IMPLEMENTED: "Implemented",
      PUNCH_COMPLETED: "Punch completed",
      PROMOTED: "Promoted",
      DECLARE_IMPLEMENTATION: "Implementation declared",
      REJECT_IMPLEMENTATION: "Implementation sent back",
      DRAFTED: "Drafted",
      UNDER_REVIEW: "Under review",
      DISTRIBUTE_FOR_RESPONSE: "Distributed for response",
    },
    findingClasses: {
      NEGATIVE_OBSERVATION: "Negative observation",
      COMMENT: "Comment",
      POSITIVE_OBSERVATION: "Positive observation",
      OFI: "Opportunity for improvement",
    },
    lineOutcomes: {
      ACCEPTED: "Accepted",
      ACCEPTED_WITH_COMMENTS: "Accepted with comments",
      REJECTED: "Rejected",
      CANCELLED: "Cancelled",
      NOT_ATTENDED: "Not attended",
    },
    actionTypes: {
      NCR: "Non-conformance report",
      CAR: "Corrective action request",
      PAR: "Preventive action request",
      OFI: "Opportunity for improvement",
      SURVEILLANCE_FINDING: "Surveillance finding",
    },
    dispositionTypes: {
      REWORK: "Rework",
      REPAIR: "Repair",
      SCRAP: "Scrap",
      USE_AS_IS: "Use as is",
      RETURN_TO_VENDOR: "Return to vendor",
    },
    rootCauseCategories: {
      MAN: "People",
      METHOD: "Method",
      MACHINE: "Machine",
      MATERIAL: "Material",
      MEASUREMENT: "Measurement",
      ENVIRONMENT: "Environment",
    },
    promotionTypes: {
      NONE: "No escalation suggested",
      NCR: "NCR suggested",
      CAR: "CAR suggested",
    },
    negotiationMoves: {
      ACKNOWLEDGE: "Acknowledged",
      PROPOSE: "Proposed",
      CONFIRM: "Confirmed",
      REJECT: "Refused",
    },
    reviewMoves: {
      COMMENT: "Commented",
      CONSULT: "Internal consultation",
      ACCEPT: "Accepted",
      ACCEPT_ITEM: "Accepted item",
      OBJECT: "Objected",
      OBJECT_ITEM: "Objected to item",
    },
    outcomes: {
      ACCEPTED: "Accepted",
      ACCEPTED_WITH_COMMENTS: "Accepted with comments",
      REJECTED: "Rejected",
    },
    effectiveness: {
      EFFECTIVE: "Effective",
      NOT_EFFECTIVE: "Not effective",
    },
    /** A state, class or category the dictionary does not know. Shown, never swallowed. */
    fallback: (token: string): string =>
      token
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
  },
} as const;

/** Render a canonical enum token through the dictionary, falling back visibly. */
export function term(token: string | null | undefined): string {
  if (token === null || token === undefined || token.length === 0) {
    return "";
  }
  const tables: Record<string, Record<string, string>> = {
    state: strings.terms.workflowStates,
    findingClass: strings.terms.findingClasses,
    lineOutcome: strings.terms.lineOutcomes,
    actionType: strings.terms.actionTypes,
    disposition: strings.terms.dispositionTypes,
    rootCause: strings.terms.rootCauseCategories,
    promotion: strings.terms.promotionTypes,
    negotiation: strings.terms.negotiationMoves,
    review: strings.terms.reviewMoves,
    outcome: strings.terms.outcomes,
    effectiveness: strings.terms.effectiveness,
  };
  const merged: Record<string, string> = {};
  for (const table of Object.values(tables)) {
    for (const [key, value] of Object.entries(table)) {
      merged[key] ??= value;
    }
  }
  return merged[token] ?? strings.terms.fallback(token);
}
